import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { TableCell, TableHead, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ArrowUp, ChevronDown, FileText, MoreHorizontal, X } from 'lucide-react';
import { IoMdClose, IoIosArrowRoundUp } from 'react-icons/io';
import { FaPrint } from 'react-icons/fa';
import InfoIcon from '../../assets/icons/InfoGrey.svg';
import Pencil from '../../assets/icons/pencil.svg';
import View from '../../assets/icons/view.svg';
import Collection from '../../assets/icons/collection.svg';
import Duplicate from '../../assets/icons/duplicate.svg';
import DuplicateDisabled from '../../assets/icons/duplicate-disabled.svg';
import Archive from '../../assets/icons/archive.svg';
import TrashCan from '../../assets/icons/trash1.svg';
import PlusIcon from '../../assets/icons/add.svg';
import UploadIcon from '../../assets/icons/upload-icon.svg';
import DocumentIcon from '../../assets/icons/file.svg';
import ShareIcon from '../../assets/icons/share-icon.svg';
import TrashCanDynamic from '../../assets/icons/trash-can-dynamic.svg';
import HoverableItem from './project_overview_table_hoverable_item';
// import NoteCell from '../data-table/NoteCell';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox-shadcn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../UI/calendar';
import moment from 'moment';
import { toast } from 'sonner';
import '../reactDatePicker/ReactDatePicker.css';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip-shadcn';
import { addNewStatus } from '../../api/statuses';
import { initial_statuses } from '../../initials/initials';
import { Config } from '../../utils';
import {
  addDraperyOrderUpload,
  deleteDraperyOrderUpload,
  editDraperyOrderUpload,
} from '../../api/drapery-order-upload';
import { generatePresignedUrl } from '../../api/file-store';
import { getTempUrl } from '../../helper/imageHelper';
import { componentUpdatePurchasedGood } from '../../api/purchased-goods';
import UploadErrorBanner from '../UI/upload-error-banner';
import type {
  ProjectOverviewColumnKey,
  ProjectOverviewColumnVisibility,
} from './column_visibility';
import { isColumnVisible } from './column_visibility';
import Notebook from '../notebook/Notebook';
import {
  applyRoleAssignmentSelection,
  getRoleAssignmentSelectedOptionKey,
  normalizeRoleAssignments,
  type RoleAssignmentOptionsByRoleId,
  type RoleAssignmentRole,
} from '../../helper/roleAssignments';
import RoleAssignmentDropdown from '../role_assignment/role_assignment_dropdown';
import { InvitePanelMode } from '../../pages/team_settings';
import { ScrollToPosition } from '../../containers/application';
import EstimateStatusCell from '../estimating/EstimateStatusCell';
import CustomColumnTextCell from '../orderingAndReceiving/custom_column_text_cell';
import {
  CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID,
  readCustomColumnValue,
  type CustomColumnId,
} from '../orderingAndReceiving/custom_columns';
import type { CustomColumnPreference } from '../../api/column-visibility-preferences';

export interface ProjectOverviewTableRowProps {
  showClient?: boolean;
  data?: any;
  actions?: React.ReactNode;
  className?: string;
  id?: number;
  rowIndex?: number;
  checkedProjects?: number[];
  columnVisibility?: ProjectOverviewColumnVisibility;
  onCheckRow?: (id: number, checked: boolean) => void;
  onActionMenu?: (value: string, type?: string, selectedProjectIds?: number[]) => void;
  handleNotCheckedBatchRow?: (id: number, project: any) => void;
  onOpenModal?: (value: string) => void;
  onItemClick?: (
    id?: number,
    item?: number,
    target?: 'summary',
    clientDisplayName?: string,
    customOrderType?: string | null
  ) => void;
  onWorkOrderClick?: (id: number) => void;
  canOpenItems?: boolean;
  canManageActions?: boolean;
  onEdited?: (projects: any[]) => void;
  updateNotebook?: (projId: number, filterOut: string) => void;
  handleEstimate?: (projectIds: number[], source: string) => void;
  qboConnected?: boolean;
  onOpenEstimatingTab?: () => void;
  onOpenEstimate?: (estimateId: number, estimate?: any) => void;
  handleOpenSinglePurchasedGoods?: (val: number | null) => void;
  onClientClick?: (id?: number) => void;
  tableStatuses?: any[];
  handleTriggerRefetch?: () => void;
  handleTriggerProjectRefetch?: () => void;
  handleOpenComponents?: (id: number | null) => void;
  componentsModalProject?: boolean;
  handleSaveComponents?: (components: any) => void;
  accountUser?: any;
  roleAssignmentColumns?: RoleAssignmentRole[];
  assignmentOptionsByRoleId?: RoleAssignmentOptionsByRoleId;
  accountIsAdmin?: boolean;
  canAccessTeamSettings?: boolean;
  handleOpenInvitePanel?(mode: InvitePanelMode, role?: any): void;
  roleAssignmentTeamMembers?: any[];
  roleAssignmentAssignees?: any[];
  handleUpdateTeamMember?(val: any, member: any, role: any): void;
  handleUpdateAssignee?(val: any, assignee: any, role: any): void;
  customColumns?: CustomColumnPreference[];
  onCustomColumnValueChange?: (
    project: any,
    columnId: CustomColumnId,
    value: string
  ) => Promise<boolean> | boolean | void;
}

const stickyHeaderCellClass = 'sticky !right-0 z-[70] !bg-table-header';
const stickyCellClass = 'sticky right-0 z-10 bg-background/60';
const assigneeColumnsReleaseDate = new Date('2026-03-02');
const thirtyDaysAfterAssigneeColumnsRelease = new Date(assigneeColumnsReleaseDate);
thirtyDaysAfterAssigneeColumnsRelease.setDate(thirtyDaysAfterAssigneeColumnsRelease.getDate() + 30);
const showAssigneeColumnsNewBadge = thirtyDaysAfterAssigneeColumnsRelease > new Date();

const getDisplayName = (client?: any): string => {
  if (!client || typeof client !== 'object') return '';
  if (typeof client.customDisplayName === 'string' && client.customDisplayName.trim()) {
    return client.customDisplayName;
  }
  return client.displayName || [client.firstName, client.lastName].filter(Boolean).join(' ');
};

const getParentChain = (client?: any, parentClients?: any[]): any[] => {
  const parents: any[] = [];
  const seenIds = new Set<number>();

  const addParent = (parent: any) => {
    if (!parent || typeof parent !== 'object') return;
    const id = parent.id;
    if (typeof id === 'number') {
      if (seenIds.has(id)) return;
      seenIds.add(id);
    }
    parents.push(parent);
  };

  const parentList = Array.isArray(parentClients) ? parentClients : client?.parentClients;
  if (Array.isArray(parentList) && parentList.length > 0) {
    parentList.forEach(addParent);
    return parents;
  }

  let current = client?.parentClient || client?.parent;
  while (current) {
    addParent(current);
    current = current.parentClient || current.parent;
  }
  return parents;
};

const formatDate = (value: any): string => {
  if (!value) return 'MM/DD/YY';
  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const dd = String(parsed.getDate()).padStart(2, '0');
      const yy = String(parsed.getFullYear()).slice(-2);
      return `${mm}/${dd}/${yy}`;
    }
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

const toCalendarDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const getBooleanTextClass = (value: any): string => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  if (['yes', 'y', 'true'].includes(normalized)) return 'text-emerald-600';
  if (['no', 'n', 'false'].includes(normalized)) return 'text-rose-500';
  return 'text-foreground';
};

const isBooleanLike = (value: any): boolean => {
  if (value === undefined || value === null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === 'false';
};

const normalizeInvoicePaidValue = (value?: string | null): string => {
  if (typeof value !== 'string') return 'Not Created';
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized === 'select' || normalized === 'not created') return 'Not Created';
  if (normalized === 'paid' || normalized === 'yes') return 'Paid';
  if (normalized === 'partial') return 'Partial';
  if (normalized === 'not paid' || normalized === 'no') return 'Not Paid';
  return value;
};

const EstimatesMovedDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGoToEstimating?: () => void;
}> = ({ open, onOpenChange, onGoToEstimating }) => {
  const handleGoToEstimating = () => {
    onOpenChange(false);
    onGoToEstimating?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px]" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Estimates Have Moved</DialogTitle>
          <DialogDescription className="leading-5">
            To create an estimate, go to the{' '}
            <strong className="font-semibold text-foreground">Estimating</strong> tab and click{' '}
            <strong className="font-semibold text-foreground">+ New Estimate</strong>. You can
            select items from your project there.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="font-semibold"
            onClick={() => onOpenChange(false)}
          >
            Got it
          </Button>
          <Button type="button" className="font-semibold" onClick={handleGoToEstimating}>
            Go to Estimating
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const dateClearButtonClassName =
  'inline-flex h-3.5 w-3.5 items-center justify-center rounded text-[#111827] opacity-60 transition-opacity group-hover/date:opacity-100 hover:text-rose-600 hover:bg-transparent focus:bg-transparent';
// const dateClearButtonAbsoluteClassName = `absolute right-2 top-1/2 -translate-y-1/2 ${dateClearButtonClassName}`;
const inlineDateTriggerClassName =
  'hoverable_item_label editInlineField leftAlign status-wrap-span w-full border-0 bg-transparent p-0 shadow-none outline-none hover:bg-transparent';

const coerceNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const editableItemTypes = new Set([
  'Bed Skirt',
  'Cornice',
  'Coverlet',
  'Cushion',
  'Drapery',
  'Duvet',
  'Pillow',
  'Roller Shade',
  'Roman Shade',
  'Table Skirt',
  'Upholstery',
  'Valance',
  'Woven Shade',
  'Other',
]);

const hasHardwareTabValues = (hardware: any, draperyOrder?: any) => {
  const checkItems = (items: any) =>
    Array.isArray(items) &&
    items.some((item: any) => {
      const finish = (item.finish || '').trim();
      const quantity = (item.quantity || '').trim();
      const description = (item.description || '').trim();
      return finish !== '' || quantity !== '' || description !== '';
    });

  const pricingRowHasValues = (item: any) => {
    if (!item) return false;
    const estimateDescription = (item.estimateDescription || '').trim();
    const cost = String(item.cost ?? '').trim();
    const quantity = String(item.quantity ?? '').trim();
    const markup = String(item.markup ?? '').trim();
    const customerPrice = String(item.customerPrice ?? '').trim();

    const quantityHasValue = quantity !== '' && quantity !== '1';
    const markupHasValue = markup !== '' && markup !== '0';

    return (
      estimateDescription !== '' ||
      cost !== '' ||
      customerPrice !== '' ||
      quantityHasValue ||
      markupHasValue
    );
  };

  const useHardwareTable =
    hardware && (!draperyOrder || draperyOrder.orderType !== 'roman-shade-order');
  const pricingSource = useHardwareTable ? hardware : draperyOrder;
  const mainPricingHasValues = pricingRowHasValues({
    estimateDescription: pricingSource?.hardwareDescription,
    cost: pricingSource?.hardwareUnitPrice,
    quantity: pricingSource?.hardwareQuantity,
    markup: pricingSource?.hardwareMarkup,
    customerPrice: pricingSource?.hardwareCustomerFacingPrice,
  });

  const hardwareSih = pricingSource ? (pricingSource as any).hardwareSih : undefined;
  const shippingHasValues = hardwareSih?.active ? pricingRowHasValues(hardwareSih) : false;

  return (
    (hardware && checkItems(hardware.rods)) ||
    (hardware && checkItems(hardware.otherItems)) ||
    mainPricingHasValues ||
    shippingHasValues
  );
};

const ALLOWED_IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'svg', 'webp'];
const MAX_FILE_SIZE_KB = 10240;
const PDF_CONTENT_TYPE = 'application/pdf';
const PDF_UPLOAD_ERROR_MESSAGE = 'Failed to upload PDF. Please try again.';

type SortTableState = {
  field: string;
  position: number;
};

export const ProjectOverviewTableHeader: React.FC<{
  enableSorting?: boolean;
  showClient?: boolean;
  checkedProjects?: number[];
  totalRows?: number;
  onCheckAllRows?: (checked: boolean) => void;
  columnVisibility?: ProjectOverviewColumnVisibility;
  projectList?: ProjectOverviewTableRowProps['data'][];
  onActionSelected?: (value: string, type?: string, selectedProjectIds?: number[]) => void;
  onOpenModal?: (value: string) => void;
  handleEstimate?: (projectIds: number[], source: string) => void;
  handleProcessEstimate?: () => void;
  onOpenEstimatingTab?: () => void;
  isConnected?: boolean;
  handlePrint?: () => void;
  openPrintModal?: (value: boolean) => void;
  sortTable?: SortTableState;
  onSortLabel?: (field: string) => void;
  onSortArrow?: (field: string) => void;
  canOpenItems?: boolean;
  canManageActions?: boolean;
  roleAssignmentColumns?: RoleAssignmentRole[];
  customColumns?: CustomColumnPreference[];
  backToTop?: boolean;
  pageScrollTo?(position: ScrollToPosition, val: number): void;
}> = ({
  enableSorting = true,
  showClient = true,
  checkedProjects = [],
  totalRows = 0,
  onCheckAllRows,
  columnVisibility,
  projectList = [],
  onActionSelected,
  onOpenModal,
  onOpenEstimatingTab,
  handlePrint,
  openPrintModal,
  sortTable,
  onSortLabel,
  onSortArrow,
  canOpenItems = true,
  canManageActions = true,
  roleAssignmentColumns = [],
  customColumns = [],
  backToTop,
  pageScrollTo,
}) => {
  const headerCellClass = 'right-auto';
  const headerQaPrefix = 'project-overview-header';
  const isChecked = checkedProjects.length > 0;
  const isIndeterminate = checkedProjects.length > 0 && checkedProjects.length < totalRows;
  const showColumn = (key: ProjectOverviewColumnKey) => isColumnVisible(columnVisibility, key);
  const getSortState = (field: string) => (sortTable?.field === field ? sortTable.position : 0);
  const isActiveSort = (field: string) => sortTable?.field === field;
  const getArrowClass = () => 'arrow';
  const getArrowTooltip = (field: string) =>
    getSortState(field) === 2 ? 'Sort by descending order' : 'Sort by ascending order';
  const getLabelClass = (field: string) =>
    cn(
      'underline underline-offset-2 font-semibold',
      isActiveSort(field) ? 'text-slate-700' : 'text-slate-500'
    );
  const renderLabel = (label: string, tooltip: string, field: string) =>
    !enableSorting ? (
      <span className="font-semibold text-slate-600">{label}</span>
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn('bg-transparent p-0', getLabelClass(field))}
            onClick={() => onSortLabel?.(field)}
            data-qa={`${headerQaPrefix}-field-${field}`}
          >
            {label}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6}>
          <span>{tooltip}</span>
        </TooltipContent>
      </Tooltip>
    );
  const renderArrow = (field: string) => {
    if (!enableSorting) return null;
    if (!isActiveSort(field)) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="sort_arrow"
            onClick={() => onSortArrow?.(field)}
            role="button"
            tabIndex={0}
            data-qa={`${headerQaPrefix}-sort-arrow-${field}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSortArrow?.(field);
              }
            }}
          >
            <IoIosArrowRoundUp
              style={{
                fontSize: '20px',
                transform: getSortState(field) === 1 ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
              className={getArrowClass()}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6}>
          <span>{getArrowTooltip(field)}</span>
        </TooltipContent>
      </Tooltip>
    );
  };
  const isOverviewPage =
    typeof window !== 'undefined' && window.location.pathname.includes('overview');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [estimatesMovedDialogOpen, setEstimatesMovedDialogOpen] = useState(false);
  const [actionsDropdownStyle, setActionsDropdownStyle] = useState<React.CSSProperties | null>(
    null
  );
  const actionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const actionsDropdownRef = useRef<HTMLDivElement | null>(null);

  const getProjectId = (project: any) => {
    const itemId = coerceNumericId(project?.itemId);
    if (itemId !== null) return itemId;
    const id = coerceNumericId(project?.id);
    if (id !== null) return id;
    return null;
  };

  const selectedProjects = projectList.filter((project) => {
    const id = getProjectId(project);
    return typeof id === 'number' && checkedProjects.includes(id);
  });

  const allArchived = selectedProjects.length > 0 && selectedProjects.every((p) => p?.archived);
  const hasPricingPermission = canOpenItems;
  const canUseCoreActions = canManageActions || !hasPricingPermission;
  const canUsePricingActions = hasPricingPermission && canManageActions;
  const canOpenBatchActions = !isOverviewPage || canUseCoreActions;
  const hasDuplicateBlocked = selectedProjects.some(
    (project) =>
      project?.orderType === 'roman-shade-order' &&
      project?.newRomanShade !== 1 &&
      (project?.components?.some((entry: any) => entry?.item?.trim?.() === 'TRIM FABRIC') ||
        hasHardwareTabValues(project?.draperyOrderHardware, project))
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsDropdownRef.current &&
        actionsButtonRef.current &&
        !actionsDropdownRef.current.contains(event.target as Node) &&
        !actionsButtonRef.current.contains(event.target as Node)
      ) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!actionsOpen) return;
    const updatePosition = () => {
      const buttonEl = actionsButtonRef.current;
      if (!buttonEl) return;
      const rect = buttonEl.getBoundingClientRect();
      const top = rect.bottom + 8;
      const left = rect.left;
      setActionsDropdownStyle({
        position: 'fixed',
        top,
        left,
        zIndex: 9999,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [actionsOpen]);

  useEffect(() => {
    if (checkedProjects.length < 1) {
      setActionsOpen(false);
    }
  }, [checkedProjects.length]);

  useEffect(() => {
    if (!canUseCoreActions) {
      setActionsOpen(false);
    }
  }, [canUseCoreActions]);

  const handleBatchAction = (value: string) => {
    if (!canUseCoreActions) {
      return;
    }
    if (typeof onActionSelected === 'function') {
      onActionSelected(value);
    }
    setActionsOpen(false);
  };

  const handleArchiveAction = () => {
    if (!canUseCoreActions) {
      return;
    }
    if (allArchived) {
      if (typeof onActionSelected === 'function') {
        onActionSelected('Unarchive');
      }
    } else {
      onOpenModal?.('Archive');
    }
    setActionsOpen(false);
  };

  const handleDeleteAction = () => {
    if (!canUseCoreActions) {
      return;
    }
    onOpenModal?.('Delete');
    setActionsOpen(false);
  };

  const handleDuplicateAction = () => {
    if (!canUsePricingActions) {
      return;
    }
    if (allArchived) return;
    if (hasDuplicateBlocked) {
      onOpenModal?.('DuplicateBlocked');
      setActionsOpen(false);
      return;
    }
    if (typeof onActionSelected === 'function') {
      onActionSelected('Duplicate');
    }
    setActionsOpen(false);
  };

  const handleMovedEstimateAction = () => {
    setEstimatesMovedDialogOpen(true);
    setActionsOpen(false);
  };

  const handlePrintAction = () => {
    if (!canUsePricingActions) {
      return;
    }
    if (!selectedProjects.length) return;
    if (!selectedProjects.every((project) => project?.hasOwnProperty?.('workOrderId'))) {
      openPrintModal?.(true);
      setActionsOpen(false);
      return;
    }
    handlePrint?.();
    setActionsOpen(false);
  };

  return (
    <>
      <TableRow className="bg-[#F8FAFC] border-b border-[#D5DDE5] project-overview-header">
        <TableHead
          className={cn('w-10 px-0 py-0 po-th-select', headerCellClass)}
          data-qa={`${headerQaPrefix}-field-select`}
        >
          <div className="flex items-center justify-center">
            <Checkbox
              aria-label="Select all"
              name="markAll"
              data-qa="project-overview-checkbox-all"
              checked={isIndeterminate ? 'indeterminate' : isChecked}
              onCheckedChange={(checked) => onCheckAllRows && onCheckAllRows(checked === true)}
            />
          </div>
        </TableHead>
        {showClient && showColumn('client') && (
          <TableHead
            className={cn(
              'min-w-0 px-[12px] text-[12px] font-semibold text-slate-600 po-th-client',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-client-parent`}
          >
            <div className="flex items-center leading-tight text-[12px]">
              {renderLabel('Client', 'Sort by Client', 'clientId')}
              <span className="mx-1 text-slate-400">|</span>
              {renderLabel('Parent', 'Sort by Parent', 'parentId')}
              {(isActiveSort('clientId') || isActiveSort('parentId')) &&
                renderArrow(sortTable?.field === 'clientId' ? 'clientId' : 'parentId')}
            </div>
          </TableHead>
        )}
        {showColumn('item') && (
          <TableHead
            className={cn(
              'min-w-[130px] px-[12px] text-[12px] font-semibold text-slate-600 po-th-item',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-item-room`}
          >
            {checkedProjects.length > 0 ? (
              <>
                <div className="table_header_cell min-w-0 overflow-hidden flex-nowrap !gap-3">
                  <div className="selected_orders_title min-w-0">
                    <span className="block truncate">{checkedProjects.length} selected</span>
                  </div>
                  <div className="selected_orders_actions shrink-0">
                    <button
                      ref={actionsButtonRef}
                      type="button"
                      className="!h-8 whitespace-nowrap"
                      disabled={!canOpenBatchActions}
                      onClick={() => setActionsOpen((prev) => !prev)}
                    >
                      <span className="whitespace-nowrap">Batch Actions</span>
                      <ChevronDown size={16} />
                    </button>
                  </div>
                </div>
                {actionsOpen &&
                  actionsDropdownStyle &&
                  typeof document !== 'undefined' &&
                  createPortal(
                    <div
                      ref={actionsDropdownRef}
                      className="header_actions_dropdown_portal active"
                      style={actionsDropdownStyle ?? undefined}
                    >
                      {!isOverviewPage && (
                        <div
                          className="header_actions_dropdown_row text-[#8B98AB] hover:!text-black"
                          onClick={handleMovedEstimateAction}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              width: '100%',
                            }}
                          >
                            <FileText className="h-[18px] w-[18px] shrink-0 text-current" />
                            <span>Create Estimate</span>
                            <InfoIcon width={18} height={18} style={{ marginLeft: 'auto' }} />
                          </div>
                        </div>
                      )}
                      <div
                        onClick={() => handleBatchAction('Batch Edit')}
                        className={`header_actions_dropdown_row ${
                          !canUseCoreActions ? 'disabled' : ''
                        }`}
                      >
                        <Collection style={{ marginRight: '8px', width: '22px', height: '22px' }} />
                        <span>Edit Selected</span>
                      </div>
                      <div className="separator" />
                      {hasPricingPermission && (
                        <div
                          onClick={handleDuplicateAction}
                          className={`header_actions_dropdown_row ${
                            !canUsePricingActions || allArchived ? 'disabled' : ''
                          }`}
                        >
                          {allArchived ? (
                            <DuplicateDisabled
                              style={{ marginRight: '8px', width: '22px', height: '22px' }}
                            />
                          ) : (
                            <Duplicate
                              style={{ marginRight: '8px', width: '22px', height: '22px' }}
                            />
                          )}
                          <span style={{ fontStyle: allArchived ? 'italic' : 'unset' }}>
                            Duplicate Selected
                          </span>
                        </div>
                      )}
                      {hasPricingPermission && (
                        <div
                          onClick={handlePrintAction}
                          className={`header_actions_dropdown_row ${
                            !canUsePricingActions ? 'disabled' : ''
                          }`}
                        >
                          <FaPrint
                            fill="#3b97b1"
                            style={{ marginRight: '8px', width: '22px', height: '22px' }}
                          />
                          <span>Batch Print Selected</span>
                        </div>
                      )}
                      <div
                        onClick={handleArchiveAction}
                        className={`header_actions_dropdown_row ${
                          !canUseCoreActions ? 'disabled' : ''
                        }`}
                      >
                        <Archive style={{ marginRight: '8px', width: '22px', height: '22px' }} />
                        <span>{allArchived ? 'Unarchive' : 'Archive'} Selected</span>
                      </div>
                      <div
                        onClick={handleDeleteAction}
                        className={`header_actions_dropdown_row ${
                          !canUseCoreActions ? 'disabled' : ''
                        }`}
                      >
                        <TrashCan style={{ marginRight: '8px', width: '22px', height: '22px' }} />
                        <span>Delete Selected</span>
                      </div>
                    </div>,
                    document.body
                  )}
              </>
            ) : (
              <div className="flex items-center leading-tight text-[12px]">
                {renderLabel('Item', 'Sort by Item', 'itemTypeId')}
                <span className="mx-1 text-slate-400">|</span>
                {renderLabel('Room', 'Sort by Room', 'roomId')}
                {(isActiveSort('itemTypeId') || isActiveSort('roomId')) &&
                  renderArrow(sortTable?.field === 'itemTypeId' ? 'itemTypeId' : 'roomId')}
              </div>
            )}
          </TableHead>
        )}
        {showColumn('status') && (
          <TableHead
            className={cn(
              'min-w-[180px] w-[180px] px-[12px] text-[12px] font-semibold text-slate-600 po-th-status',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-status-th`}
          >
            <div className="flex items-center">
              {renderLabel('Status', 'Sort by Status', 'status')}
              {renderArrow('status')}
            </div>
          </TableHead>
        )}
        {showColumn('notebook') && (
          <TableHead
            className={cn(
              'min-w-[92px] px-[12px] text-center text-[12px] font-semibold text-slate-600 po-th-notes',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-notebook`}
          >
            Notebook
          </TableHead>
        )}
        {showColumn('wopo') && (
          <TableHead
            className={cn(
              'min-w-0 px-[12px] text-center text-[12px] font-semibold text-slate-600 po-th-wopo',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-wopo`}
          >
            <div className="flex w-full items-center justify-center">
              {renderLabel('WO', 'Sort by WO', 'workOrderId')}
              {renderArrow('workOrderId')}
            </div>
          </TableHead>
        )}
        {showColumn('uploads') && (
          <TableHead
            className={cn(
              'min-w-0 px-[12px] text-center text-[12px] font-semibold text-slate-600 po-th-uploads',
              headerCellClass
            )}
          >
            Uploads
          </TableHead>
        )}
        {showColumn('estimate') && (
          <TableHead
            className={cn(
              'min-w-[196px] px-[12px] text-[12px] font-semibold text-slate-600 po-th-estimate',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-estimate`}
          >
            <div className="flex items-center">
              {renderLabel('Estimate', 'Sort by Estimate', 'estimateStatus')}
              {renderArrow('estimateStatus')}
            </div>
          </TableHead>
        )}
        {showColumn('invoice') && (
          <TableHead
            className={cn(
              'min-w-[160px] px-[12px] text-[12px] font-semibold text-slate-600 po-th-invoice',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-invoice`}
          >
            <div className="flex items-center">
              {renderLabel('Invoice', 'Sort by Invoice Status', 'paid')}
              {renderArrow('paid')}
            </div>
          </TableHead>
        )}
        {showColumn('components') && (
          <TableHead
            className={cn(
              'min-w-0 px-[12px] text-center text-[12px] font-semibold text-slate-600 po-th-components',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-components`}
          >
            <div className="flex flex-col leading-tight items-center">
              <span data-qa={`${headerQaPrefix}-field-components-title`}>Components</span>
              <div className="flex items-center gap-2 text-[11px]">
                <div className="flex items-center">
                  {renderLabel('Ordered', 'Sort by Items Ordered', 'componentsOrdered')}
                  {renderArrow('componentsOrdered')}
                </div>
                <span className="text-slate-500">/</span>
                <div className="flex items-center">
                  {renderLabel('Received', 'Sort by Items Received', 'componentsReceived')}
                  {renderArrow('componentsReceived')}
                </div>
              </div>
            </div>
          </TableHead>
        )}
        {showColumn('production') && (
          <TableHead
            className={cn(
              'min-w-0 px-[12px] text-[12px] font-semibold text-slate-600 po-th-production',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-production`}
          >
            <div className="flex flex-col leading-tight">
              <span data-qa={`${headerQaPrefix}-field-production-title`}>Production</span>
              <span
                className="text-[11px] text-slate-500"
                data-qa={`${headerQaPrefix}-field-production-range`}
              >
                Start / Finish
              </span>
            </div>
          </TableHead>
        )}
        {showColumn('install') && (
          <TableHead
            className={cn(
              'min-w-0 px-[12px] text-[12px] font-semibold text-slate-600 po-th-install',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-install`}
          >
            <div className="flex flex-col leading-tight">
              <div className="flex items-center">
                {renderLabel('Install', 'Sort by Install', 'installDate')}
                {renderArrow('installDate')}
              </div>
              <span
                className="text-[11px] text-slate-500"
                data-qa={`${headerQaPrefix}-field-install-subtitle`}
              >
                No. / Date
              </span>
            </div>
          </TableHead>
        )}
        {customColumns.map((customColumn) => {
          const visibilityKey =
            CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID[customColumn.id as CustomColumnId];
          if (!visibilityKey || !showColumn(visibilityKey)) return null;

          return (
            <TableHead
              key={`header-custom-column-${customColumn.id}`}
              className={cn(
                'min-w-0 px-[12px] text-[12px] font-semibold text-slate-600 po-th-custom',
                headerCellClass
              )}
              data-qa={`${headerQaPrefix}-field-${visibilityKey}`}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-[150px] overflow-hidden">
                    <span className="block w-full truncate cursor-default">
                      {customColumn.name}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>{customColumn.name}</TooltipContent>
              </Tooltip>
            </TableHead>
          );
        })}
        {roleAssignmentColumns.map((role) => (
          <TableHead
            key={`header-role-${role.id}`}
            className={cn(
              'po-th-role px-[8px] text-[12px] font-semibold text-slate-600',
              headerCellClass
            )}
            data-qa={`${headerQaPrefix}-field-role-${role.id}`}
          >
            <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span>{role.name}</span>
              {showAssigneeColumnsNewBadge ? <span className="new_pill">New</span> : null}
            </div>
          </TableHead>
        ))}
        <TableHead
          className={cn('w-12 text-center po-th-actions', headerCellClass, stickyHeaderCellClass)}
          data-qa={`${headerQaPrefix}-field-actions`}
        >
          {backToTop ? (
            <div className="flex items-center justify-center">
              <button
                data-qa={`back_to_top_arrow`}
                onClick={() => {
                  if (pageScrollTo) {
                    pageScrollTo('top', 0);
                  }
                }}
                type="button"
                className="text-white w-8 h-8 rounded-full bg-teal text-teal-foreground flex items-center justify-center shadow-md hover:bg-teal-dark transition-colors animate-fade-in mx-auto"
              >
                <ArrowUp className="lucide lucide-arrow-up h-4 w-4" />
              </button>
            </div>
          ) : (
            <span className="sr-only">Actions</span>
          )}
        </TableHead>
      </TableRow>
      <EstimatesMovedDialog
        open={estimatesMovedDialogOpen}
        onOpenChange={setEstimatesMovedDialogOpen}
        onGoToEstimating={onOpenEstimatingTab}
      />
    </>
  );
};

export const ProjectOverviewTableRow: React.FC<ProjectOverviewTableRowProps> = ({
  showClient = true,
  data,
  actions,
  className,
  id,
  rowIndex,
  checkedProjects = [],
  columnVisibility,
  onCheckRow,
  onActionMenu,
  handleNotCheckedBatchRow,
  onOpenModal,
  onItemClick,
  canOpenItems = true,
  canManageActions = true,
  onEdited,
  updateNotebook,
  onOpenEstimatingTab,
  onOpenEstimate,
  handleOpenSinglePurchasedGoods,
  onWorkOrderClick,
  onClientClick,
  tableStatuses,
  handleTriggerRefetch,
  handleTriggerProjectRefetch,
  handleOpenComponents,
  componentsModalProject,
  handleSaveComponents,
  roleAssignmentColumns = [],
  assignmentOptionsByRoleId = {},
  accountIsAdmin = false,
  accountUser,
  canAccessTeamSettings,
  handleOpenInvitePanel,
  roleAssignmentTeamMembers = [],
  roleAssignmentAssignees = [],
  handleUpdateTeamMember,
  handleUpdateAssignee,
  customColumns = [],
  onCustomColumnValueChange,
}) => {
  const showColumn = (key: ProjectOverviewColumnKey) => isColumnVisible(columnVisibility, key);
  const handleCustomColumnChange = (columnId: CustomColumnId, value: string) => {
    if (!canEditOrder || data?.archived || !onCustomColumnValueChange) return;
    void onCustomColumnValueChange(data, columnId, value);
  };
  type ActiveDropdown = 'status' | 'uploads' | 'invoicePaid' | 'estimateStatus' | null;
  const [activeDropdown, setActiveDropdown] = useState<ActiveDropdown>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null);
  const [estimatesMovedDialogOpen, setEstimatesMovedDialogOpen] = useState(false);
  const statusOpen = activeDropdown === 'status';
  const statusContainerRef = useRef<HTMLDivElement | null>(null);
  const statusDropdownRef = useRef<HTMLUListElement | null>(null);
  const [newStatus, setNewStatus] = useState(false);
  const [newStatusValue, setNewStatusValue] = useState('');
  const newStatusRef = useRef<HTMLInputElement | null>(null);
  const uploadRef = useRef<HTMLDivElement | null>(null);
  const uploadsButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeUploadsRef = useRef<HTMLButtonElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadsOpen = activeDropdown === 'uploads';
  const [uploadError, setUploadError] = useState<string | null>(null);
  const accountId = typeof window !== 'undefined' ? localStorage.getItem('accountId') : null;
  const invoicePaidOpen = activeDropdown === 'invoicePaid';
  const estimateStatusOpen = activeDropdown === 'estimateStatus';
  const [invoiceDateOpen, setInvoiceDateOpen] = useState(false);
  const [invoiceNumberOpen, setInvoiceNumberOpen] = useState(false);
  const [estimateDateOpen, setEstimateDateOpen] = useState(false);
  const [estimateNumberOpen, setEstimateNumberOpen] = useState(false);
  const [installNumberOpen, setInstallNumberOpen] = useState(false);
  const [openDateField, setOpenDateField] = useState<
    'productionStartDate' | 'productionFinishedDate' | 'installDate' | null
  >(null);
  const invoicePaidRef = useRef<HTMLUListElement | null>(null);
  const invoicePaidTriggerRef = useRef<HTMLDivElement | null>(null);
  const invoiceDateRef = useRef<HTMLDivElement | null>(null);
  const estimateStatusRef = useRef<HTMLUListElement | null>(null);
  const estimateStatusTriggerRef = useRef<HTMLDivElement | null>(null);
  const estimateDateRef = useRef<HTMLDivElement | null>(null);
  const clientName =
    (typeof data?.clientName === 'string' && data.clientName) ||
    (typeof data?.client === 'string' && data.client) ||
    getDisplayName(data?.client) ||
    '';
  const parentChain = getParentChain(data?.client, data?.parentClients);

  const parentChainNames = parentChain.map((parent) =>
    typeof parent === 'string' ? parent : getDisplayName(parent)
  );

  const parentName =
    parentChainNames.length > 0
      ? parentChainNames.join(' / ')
      : getDisplayName(data?.parent) ||
        getDisplayName((data as any)?.mainParent) ||
        (!isBooleanLike(data?.parent) && typeof data?.parent === 'string' ? data?.parent : '') ||
        '';
  const itemDisplayName = (() => {
    if (data?.orderType === 'purchased-goods') {
      const pgName = data?.purchasedGoodsItemType?.name || data?.itemType || '';
      const pgSupplier = data?.purchasedGoodsSupplier?.displayName || 'Supplier TBD';
      const pgWindow = data?.purchasedGoodsWindowLocationId || '';
      return pgName ? `${pgName} - ${pgSupplier} ${pgWindow ? `/ ${pgWindow}` : ''}` : '';
    }
    if (data?.itemType) {
      const location =
        data?.window || data?.locationId || data?.location || data?.locationName || '';
      if (data?.itemType === 'Drapery Order' || data?.itemType === 'Drapery') {
        return `Drapery${location ? ` / ${location}` : ''}`;
      }
      return `${data?.itemType}${location ? ` / ${location}` : ''}`;
    }
    return data?.item || '';
  })();
  const roomName = data?.roomName ?? data?.customRoom ?? data?.room ?? '';
  const statusName = data?.status ?? '';
  const workOrder = data?.workOrderNum ?? data?.workOrderId ?? data?.workOrder ?? '';
  const isDraft = data?.isDraft || false;
  const isPurchasedGoodsOrder = data?.orderType === 'purchased-goods';
  const uploads = Array.isArray(data?.draperyOrderUpload?.items)
    ? data.draperyOrderUpload.items
    : [];
  const uploadsTitle = isPurchasedGoodsOrder ? 'PO Documents' : 'Documents';
  const uploadButtonLabel = isPurchasedGoodsOrder ? 'Upload PO Document' : 'Upload File';
  const uploadsEmptyLabel = isPurchasedGoodsOrder
    ? 'No PO documents uploaded yet'
    : 'No uploads yet';
  const uploadDocumentQaPrefix = isPurchasedGoodsOrder ? 'po_document' : 'upload_document';
  const uploadSuccessToast = isPurchasedGoodsOrder
    ? {
        title: 'PO document uploaded',
        description: 'The PO document has been uploaded successfully.',
      }
    : {
        title: 'File uploaded',
        description: 'The file has been uploaded successfully.',
      };
  const deleteSuccessToast = isPurchasedGoodsOrder
    ? {
        title: 'PO document deleted',
        description: 'The PO document has been removed successfully.',
      }
    : {
        title: 'File deleted',
        description: 'The file has been removed successfully.',
      };
  const knownEstimateStatuses = new Set([
    'Not Created',
    'Created',
    'Exported to QB',
    'Sent to Customer',
  ]);
  const rawEstimateNumber = typeof data?.estimateNumber === 'string' ? data.estimateNumber : '';
  const estimateAssociations = Array.isArray(data?.estimateStatusSummary?.estimates)
    ? data.estimateStatusSummary.estimates
    : [];
  const estimateStatusValue =
    (typeof data?.estimateStatus === 'string' && data?.estimateStatus) ||
    data?.estimateStatusSummary?.status ||
    (knownEstimateStatuses.has(rawEstimateNumber) ? rawEstimateNumber : '') ||
    '';
  const invoiceStatusValue = normalizeInvoicePaidValue(data?.invoicePaid);
  const estimateNumberValue =
    data?.qbEstimateNumber ??
    (rawEstimateNumber && !knownEstimateStatuses.has(rawEstimateNumber) ? rawEstimateNumber : '');
  const hasEstimateConnection = Boolean(
    data?.estimateId ||
    data?.quipaEstimateId ||
    data?.qbEstimateId ||
    estimateAssociations.some((estimate: any) => estimate?.estimateId || estimate?.qbEstimateId)
  );
  const canEditOrder = !!onEdited;
  const roleAssignments = normalizeRoleAssignments(data?.assignments);
  const hasPricingPermission = canOpenItems;
  const canUseCoreActions = canManageActions || !hasPricingPermission;
  const canUsePricingActions = hasPricingPermission && canManageActions;
  const canOpenOrder = canUsePricingActions;
  const canEditEstimateNumber = canEditOrder && !data?.archived && !hasEstimateConnection;
  const [projectComponents, setProjectComponents] = useState<any[]>([]);
  const productionStart = formatDate(data?.productionStartDate ?? data?.productionStart ?? '');
  const productionFinish = formatDate(
    data?.productionFinishedDate ?? data?.productionFinish ?? data?.productionDuoDate ?? ''
  );
  const installNumberRaw = data?.installNumber ?? '';
  const installNumberText =
    typeof installNumberRaw === 'number' ? String(installNumberRaw) : String(installNumberRaw);
  const hasInstallNumber = installNumberText.trim() !== '';
  const installDate = formatDate(data?.installDate ?? '');
  const isProductionStartPlaceholder = productionStart === 'MM/DD/YY';
  const isProductionFinishPlaceholder = productionFinish === 'MM/DD/YY';
  const isInstallDatePlaceholder = installDate === 'MM/DD/YY';
  const invoiceSentDateToShow = !data?.invoiceDateSent
    ? null
    : moment(data?.invoiceDateSent).isValid()
      ? moment(data?.invoiceDateSent).format('MM/DD/YY')
      : data?.invoiceDateSent;
  const estimateStatusDate =
    data?.estimateDate !== undefined && data?.estimateDate !== ''
      ? data?.estimateDate
      : data?.estimateDateSent !== undefined && data?.estimateDateSent !== ''
        ? data?.estimateDateSent
        : data?.estimateStatusSummary?.statusDate;
  const estimateSentDateToShow = estimateStatusDate ? formatDate(estimateStatusDate) : null;
  const rowId = typeof id === 'number' ? id : typeof data?.itemId === 'number' ? data.itemId : -1;
  const qaToken = (value: unknown): string =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'unknown';
  const rowQa = (field: string): string => `project_overview_row_${rowId}_${field}`;
  const isOverviewPage =
    typeof window !== 'undefined' && window.location.pathname.includes('overview');
  const isChecked = rowId !== -1 && checkedProjects.includes(rowId);
  const showBatchOrEditOrder = useMemo(() => {
    if (!checkedProjects || checkedProjects.length === 0) return false;
    if (checkedProjects.length >= 2) return true;
    if (checkedProjects.length === 1 && !checkedProjects.includes(rowId)) return true;
    return false;
  }, [checkedProjects, rowId]);

  const isEditDisabled =
    data?.itemType !== 'Drapery Order' &&
    data?.itemType !== 'Drapery' &&
    !editableItemTypes.has(data?.itemType);

  // const noteValue =
  //   data?.orderType === 'purchased-goods' ? data?.purchasedGoodsNotes : (data?.notes ?? data?.note);
  // const noteRow = useMemo(
  //   () => ({
  //     original: {
  //       id: typeof data?.itemId === 'number' ? data?.itemId : data?.id,
  //       client: {
  //         notes: noteValue ?? '',
  //         notesUpdatedAt: data?.notesUpdatedAt,
  //       },
  //     },
  //   }),
  //   [data?.id, data?.itemId, data?.notesUpdatedAt, noteValue]
  // );
  // const [notesNewModal, setNotesNewModal] = useState<number | null>(null);

  // const handleUpdateNote = (value: string) => {
  //   if (!canEditOrder || !onEdited) return;
  //   const updatedProject = {
  //     ...data,
  //     ...(data?.orderType === 'purchased-goods'
  //       ? { purchasedGoodsNotes: value }
  //       : { notes: value }),
  //     ...(value ? { notesUpdatedAt: new Date() } : {}),
  //   };
  //   onEdited([updatedProject]);
  // };

  // const handleNotes = (_noteCase: any, _obj: any, _key: any, value: any) => {
  //   if (data?.archived || !canEditOrder) return;
  //   handleUpdateNote(value || '');
  // };

  // const handleNewNote3DotMenu = (value: number | null) => {
  //   setNotesNewModal(value);
  // };

  // const handleDotMenuNotes = () => {
  //   if (data?.archived) return;
  //   if (data?.orderType === 'purchased-goods') {
  //     if (data?.purchasedGoodsNotes) {
  //       handleUpdateNote('');
  //       return;
  //     }
  //   } else if (data?.notes) {
  //     handleUpdateNote('');
  //     return;
  //   }
  //   setIsNotesOpen(true);
  // };

  const handleMenuAction = (value: string, type?: string, selectedProjectIds?: number[]) => {
    if (typeof onActionMenu === 'function') {
      onActionMenu(value, type, selectedProjectIds);
    }
  };

  const handleRoleAssignmentChange = (roleId: number, selectedValue: string) => {
    if (!canEditOrder || !onEdited || data?.archived) {
      return;
    }

    const nextAssignments = applyRoleAssignmentSelection(
      roleAssignments,
      roleId,
      selectedValue,
      assignmentOptionsByRoleId
    );

    onEdited([
      {
        ...data,
        assignments: nextAssignments,
      },
    ]);
  };

  const handleOnBlurStatus = () => {
    setActiveDropdown((prev) => (prev === 'status' ? null : prev));
    setNewStatus(false);
    setNewStatusValue('');
  };

  const handleCellColor = (value?: string) => {
    let className = 'base_status_color';
    if (value) {
      const defaultStatuses =
        initial_statuses && initial_statuses.length > 0
          ? initial_statuses.map((s: any) => s.value)
          : [];
      if (!defaultStatuses.includes(value)) {
        className = 'custom_status_color';
      } else {
        className = `status_${value.split(' ').join('_').toLowerCase()}`;
      }
    }
    return className;
  };

  const getEstimateStatusClass = (value?: string) => {
    if (!value) return 'estimate_status_not_created';
    const normalized = value.toLowerCase();
    if (normalized === 'created') return 'estimate_status_created';
    if (normalized === 'exported to qb') return 'estimate_status_exported';
    if (normalized === 'sent to customer') return 'estimate_status_sent';
    return 'estimate_status_not_created';
  };

  const getInvoiceStatusClass = (value?: string) => {
    const normalized = normalizeInvoicePaidValue(value).toLowerCase();
    if (normalized === 'paid' || normalized === 'yes') return 'invoicePaid_paid';
    if (normalized === 'partial') return 'invoicePaid_partial';
    if (normalized === 'not paid' || normalized === 'no') return 'invoicePaid_not_paid';
    if (normalized === 'not created') return 'invoicePaid_not_created';
    return 'invoicePaid_not_created';
  };

  const handleChangeStatus = (value: string) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = {
      ...data,
      status: value,
    };
    onEdited([updatedProject]);
    handleOnBlurStatus();
  };

  const handleSetNewStatus = () => {
    if (!canEditOrder) return;
    if (!newStatusValue) {
      setNewStatus((prev) => !prev);
    }
  };

  const handleChangeNewStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditOrder) return;
    setNewStatusValue(e.currentTarget.value);
  };

  const handleSaveNewStatus = async () => {
    if (!canEditOrder) return;
    if (newStatusValue && newStatusValue.trim()) {
      const accountId = localStorage.getItem('accountId');
      const payload: { accountId?: number; name: string } = {
        name: newStatusValue,
      };
      if (accountId) {
        const parsedAccountId = JSON.parse(accountId);
        payload['accountId'] = parsedAccountId;
      }
      const res = await addNewStatus(payload);
      if (res && !res.error) {
        setNewStatusValue('');
        setNewStatus(false);
        if (handleTriggerRefetch) handleTriggerRefetch();
      } else {
        console.error(res.error, res.message);
      }
    }
  };

  const getDropdownMeta = (key: ActiveDropdown) => {
    switch (key) {
      case 'status':
        return {
          anchor: statusContainerRef,
          dropdown: statusDropdownRef,
          width: 220,
          maxHeight: 320,
          gap: 6,
        };
      case 'uploads':
        return {
          anchor: uploadsButtonRef,
          dropdown: uploadRef,
          width: 320,
          maxHeight: 340,
          gap: 6,
          clampLeft: true,
          placement: 'right',
        };
      case 'invoicePaid':
        return {
          anchor: invoicePaidTriggerRef,
          dropdown: invoicePaidRef,
          width: 150,
          maxHeight: 220,
          gap: 6,
        };
      case 'estimateStatus':
        return {
          anchor: estimateStatusTriggerRef,
          dropdown: estimateStatusRef,
          width: 170,
          maxHeight: 240,
          gap: 6,
        };
      default:
        return null;
    }
  };

  useEffect(() => {
    if (newStatus && newStatusRef.current) {
      newStatusRef.current.focus();
    }

    if (hasEstimateConnection && estimateNumberOpen) {
      setEstimateNumberOpen(false);
    }

    if (!activeDropdown) {
      setDropdownStyle(null);
      return;
    }

    const meta: any = getDropdownMeta(activeDropdown);
    if (!meta || !meta.anchor.current) return;

    const updatePosition = () => {
      const anchorEl = meta.anchor.current;
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      const width = meta.width;
      const gap = meta.gap ?? 6;
      const height = meta.height ?? meta.maxHeight ?? 0;
      const isRightPlacement = meta.placement === 'right';
      let top = rect.bottom + gap;
      let left = rect.left;

      if (isRightPlacement) {
        left = rect.right + gap;
        if (left + width > window.innerWidth - 8) {
          left = rect.left - width - gap;
        }
        top = rect.top;
      } else {
        const shouldDropUp =
          rect.bottom + height + gap > window.innerHeight && rect.top - height - gap > 0;
        top = shouldDropUp ? rect.top - height - gap : rect.bottom + gap;
      }

      const clampedTop = Math.min(Math.max(8, top), window.innerHeight - height - 8);
      const clampedLeft = meta.clampLeft
        ? Math.min(Math.max(8, left), window.innerWidth - width - 8)
        : left;
      setDropdownStyle({
        position: 'fixed',
        top: clampedTop,
        left: clampedLeft,
        right: 'auto',
        width,
        zIndex: 3000,
      });
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const anchorEl = meta.anchor.current;
      const dropdownEl = meta.dropdown?.current ?? null;
      if (anchorEl && anchorEl.contains(target)) return;
      if (dropdownEl && dropdownEl.contains(target)) return;
      if (activeDropdown === 'status') {
        handleOnBlurStatus();
      } else {
        setActiveDropdown(null);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown, estimateNumberOpen, hasEstimateConnection, newStatus]);

  useEffect(() => {
    let componentsArray: any[] = [];
    if (data?.components && data.components.length > 0) {
      componentsArray = data.components
        .map((c: any) => ({ ...c }))
        .filter((c: any) => !c.isDeleted);
    }

    const mappedPurchasedGoodsComponents =
      data?.purchasedGoodsComponentsType === 'components-build'
        ? data?.purchasedGoodsComponentsBuild && data?.purchasedGoodsComponentsBuild?.length > 0
          ? data?.purchasedGoodsComponentsBuild?.map((i: any) => {
              return {
                ...i,
                item:
                  i?.item?.name ||
                  `${data?.purchasedGoodsItemType?.name}${
                    data?.purchasedGoodsSupplier
                      ? ' - ' + data?.purchasedGoodsSupplier?.displayName
                      : ''
                  }` ||
                  '',
              };
            })
          : []
        : data?.purchasedGoodsComponents?.map((i: any) => {
            return {
              id: i.id || '',
              item:
                i.item ||
                `${data?.purchasedGoodsItemType?.name}${
                  data?.purchasedGoodsSupplier
                    ? ' - ' + data?.purchasedGoodsSupplier?.displayName
                    : ''
                }` ||
                '',
              po: i.po || '',
              poGenerated: i.poGenerated || false,
              orderedDate: i.orderedDate || '',
              eta: i.eta || '',
              receivedDate: i.receivedDate || '',
              notes: i.notes || '',
              createdAt: i.createdAt || '',
              quantity: i.qty || '',
              isStaticHardware: false,
              fabric: null,
              headrail: null,
              qty: i.qty || '',
              cost: i.cost || '',
              type: i.type || '',
              markup: i.markup || '',
              calculatedPrice: i.calculatedPrice || '',
              customerFacingPrice: i.customerFacingPrice || '',
              estimateDescription: i.estimateDescription || '',
              purchaser: data?.purchasedGoodsPurchaser || 1,
            };
          }) || [];

    const headrailWorkroom =
      data?.orderType !== 'roman-shade-order' ||
      (data?.draperyOrderHeadrail &&
        (data?.draperyOrderHeadrail.purchaser === 1 || data?.draperyOrderHeadrail.receiver === 1));

    if (!headrailWorkroom) {
      componentsArray = componentsArray.filter((component) => component.item !== 'HEADRAIL');
    }

    const partsAndSuppliesWorkroom =
      data?.orderType !== 'custom-order' ||
      !data?.draperyOrderHardware ||
      data?.draperyOrderHardware.purchaser === 1 ||
      data?.draperyOrderHardware.receiver === 1;

    if (!partsAndSuppliesWorkroom) {
      componentsArray = componentsArray.filter(
        (component) => component.item !== 'PARTS & SUPPLIES'
      );
    }

    if (data?.orderType === 'purchased-goods') {
      setProjectComponents(mappedPurchasedGoodsComponents);
    } else {
      setProjectComponents(componentsArray);
    }
  }, [data, componentsModalProject]);

  const hasValue = (value: any): boolean =>
    value !== undefined && value !== null && String(value).trim() !== '';

  const componentsReceivedData = useMemo(() => {
    if (!projectComponents || projectComponents.length === 0) return false;
    return projectComponents.every((component: any) => hasValue(component?.receivedDate));
  }, [projectComponents]);

  const handleCheckComponentsCase = useMemo(() => {
    if (!projectComponents || projectComponents.length === 0) return false;

    const withFabric = projectComponents.filter((component: any) => component?.fabric);
    const withoutFabricAndHardware = projectComponents.filter(
      (component: any) => !component?.fabric && !component?.isStaticHardware
    );
    const withHardware = projectComponents.filter((component: any) => component?.isStaticHardware);

    const checkWithFabric =
      withFabric.length === 0 ||
      withFabric.every(
        (component: any) => component?.fabric?.orderOrigin === 2 || hasValue(component?.orderedDate)
      );

    const checkWithoutFabricAndHardware =
      withoutFabricAndHardware.length === 0 ||
      withoutFabricAndHardware.every((component: any) => hasValue(component?.orderedDate)) ||
      withoutFabricAndHardware.every((component: any) => component?.purchaser === 2);

    const checkWithHardware =
      withHardware.length === 0 ||
      withHardware.every((component: any) => {
        if (hasValue(component?.orderedDate)) {
          return true;
        }
        if (data?.orderType === 'roman-shade-order') {
          return data?.draperyOrderHeadrail?.purchaser === 2;
        }
        return data?.draperyOrderHardware?.purchaser === 2;
      });

    return checkWithFabric && checkWithoutFabricAndHardware && checkWithHardware;
  }, [
    data?.draperyOrderHardware?.purchaser,
    data?.draperyOrderHeadrail?.purchaser,
    data?.orderType,
    projectComponents,
  ]);

  const componentsOrderedData = componentsReceivedData || handleCheckComponentsCase;

  const handleChooseFile = () => {
    if (!canEditOrder) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditOrder) return;
    const files = e.target.files || [];
    handleFile(files);
  };

  const convertToPng = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Canvas context is not supported.');
          return;
        }
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.drawImage(image, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject('Conversion to PNG failed.');
              return;
            }
            const pngFile = new File([blob], file.name.replace(/\.\w+$/, '.png'));
            const pngUrl = URL.createObjectURL(pngFile);
            resolve(pngUrl);
          },
          'image/png',
          1
        );
      };
      image.onerror = () => {
        reject('Image loading failed.');
      };
      image.src = URL.createObjectURL(file);
    });
  };

  const buildRemoteSourcePath = (remoteKey: string): string => {
    if (!remoteKey) {
      return '';
    }
    return remoteKey.startsWith('/') ? remoteKey : `/${remoteKey}`;
  };

  const sanitizeFileName = (fileName: string): string => {
    if (!fileName) {
      return 'upload.pdf';
    }
    return fileName.replace(/[^a-z0-9._() -]/gi, '_');
  };

  const buildRemoteFileName = (fileName: string): string => {
    const sanitized = sanitizeFileName(fileName || 'upload.pdf');
    const parts = sanitized.split('.');
    const extension = parts.length > 1 ? parts.pop() || 'pdf' : 'pdf';
    const base = parts.join('.') || 'upload';
    return `${base}.${extension}`;
  };

  const inferUploadTypeFromFile = (file: File): string => {
    const fileName = (file.name || '').toLowerCase();
    const extension = fileName.split('.').pop() || '';
    if (extension) {
      if (extension === 'jpeg') return 'jpg';
      return extension;
    }
    const mime = (file.type || '').toLowerCase();
    if (mime.includes('pdf')) return 'pdf';
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
    if (mime.includes('png')) return 'png';
    if (mime.includes('svg')) return 'svg';
    if (mime.includes('webp')) return 'webp';
    return 'application/octet-stream';
  };

  const uploadFileWithPresign = async (file: File, uploadType: 'pdf' | 'image') => {
    if (!accountId) throw new Error('Missing accountId');
    const storeId = `${accountId}_${uploadType === 'pdf' ? 'pdfs' : 'images'}`;
    const inferredType = inferUploadTypeFromFile(file);
    const fallbackName = uploadType === 'pdf' ? 'upload.pdf' : `upload.${inferredType || 'png'}`;
    const generatedFileName = buildRemoteFileName(file.name || fallbackName);
    const contentType =
      file.type || (uploadType === 'pdf' ? PDF_CONTENT_TYPE : 'application/octet-stream');

    const presignResponse = await generatePresignedUrl({
      storeId,
      fileName: generatedFileName,
      method: 'PUT',
      contentType,
      preserveFileName: true,
    });
    const resolvedFileName = presignResponse.fileName || generatedFileName;

    const response = await fetch(presignResponse.url, {
      method: presignResponse.method || 'PUT',
      headers: {
        'Content-Type': contentType,
        'Content-Disposition': `attachment; filename="${resolvedFileName}"`,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file via presigned URL. Status: ${response.status}`);
    }

    const remoteKey = presignResponse.key;
    return {
      remoteKey,
      remoteFileName: resolvedFileName,
      remoteSource: buildRemoteSourcePath(remoteKey),
    };
  };

  const inferUploadTypeFromSource = (source: string): string => {
    const extension = (source || '').split('?')[0].split('.').pop()?.toLowerCase();
    if (!extension) return 'application/octet-stream';
    if (extension === 'jpg' || extension === 'jpeg') return 'jpg';
    if (extension === 'png') return 'png';
    if (extension === 'svg') return 'svg';
    if (extension === 'webp') return 'webp';
    if (extension === 'pdf') return 'pdf';
    return extension;
  };

  const handleFile = async (files: File[] | FileList) => {
    if (!canEditOrder) return;
    if (!files || files.length === 0) {
      return;
    }
    setUploadError(null);

    const filesToProcess = Array.from(files);
    const preparedUploads: any[] = [];

    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      const fileName = (file.name || '').toLowerCase();
      const extension = fileName.split('.').pop() || '';
      const mimeType = (file.type || '').toLowerCase();
      const isPdf = extension === 'pdf' || mimeType === 'application/pdf';
      const isImageExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext));

      if (!isPdf && !isImageExtension) {
        setUploadError('Valid file types: JPEG, PNG, SVG, WEBP, PDF.');
        continue;
      }

      if (file.size / 1024 > MAX_FILE_SIZE_KB) {
        setUploadError('Max allowed size is 10MB.');
        continue;
      }

      const normalizedFileType = inferUploadTypeFromFile(file);

      if (isPdf) {
        const upload: any = file;
        upload.source = URL.createObjectURL(file);
        upload.description = '';
        upload.fromRest = false;
        upload.isPdf = true;
        upload.blobToSend = file;
        upload.fileType = normalizedFileType;
        upload.mimeType = file.type || PDF_CONTENT_TYPE;

        if (!Config.fileStoreUrl.includes('localhost')) {
          try {
            const remoteInfo = await uploadFileWithPresign(file, 'pdf');
            upload.remoteKey = remoteInfo.remoteKey;
            upload.remoteFileName = remoteInfo.remoteFileName;
            upload.remoteSource = remoteInfo.remoteSource;
            upload.remoteUploaded = true;
          } catch (error) {
            console.error('Presigned PDF upload failed:', error);
            setUploadError(PDF_UPLOAD_ERROR_MESSAGE);
            continue;
          }
        }

        preparedUploads.push(upload);
        continue;
      }

      let obj: any = {};

      if (!Config.fileStoreUrl.includes('localhost')) {
        try {
          const remoteInfo = await uploadFileWithPresign(file, 'image');
          obj = file;
          obj.source = URL.createObjectURL(file);
          obj.description = '';
          obj.fromRest = false;
          obj.fileType = normalizedFileType;
          obj.mimeType = file.type || 'application/octet-stream';
          obj.blobToSend = file;
          obj.remoteKey = remoteInfo.remoteKey;
          obj.remoteFileName = remoteInfo.remoteFileName;
          obj.remoteSource = remoteInfo.remoteSource;
          obj.remoteUploaded = true;
        } catch (error) {
          console.error('Presigned image upload failed:', error);
          setUploadError('Failed to upload image. Please try again.');
          continue;
        }
      } else if (fileName.endsWith('.svg') || fileName.endsWith('.webp')) {
        try {
          const pngUrl = await convertToPng(file);
          obj = file;
          obj.source = pngUrl;
          obj.description = '';
          obj.fromRest = false;
          obj.fileType = normalizedFileType;
          obj.mimeType = file.type;
          obj.blobToSend = file;
        } catch (error) {
          console.error(error);
        }
      } else {
        obj = file;
        obj.source = URL.createObjectURL(file);
        obj.description = '';
        obj.fromRest = false;
        obj.fileType = normalizedFileType;
        obj.mimeType = file.type;
        obj.blobToSend = file;
      }

      if (Object.keys(obj).length > 0) {
        preparedUploads.push(obj);
      }
    }

    if (!accountId || preparedUploads.length === 0) {
      return;
    }

    let uploadRecordId = data?.draperyOrderUpload?.id;
    let existingItems = Array.isArray(data?.draperyOrderUpload?.items)
      ? [...data.draperyOrderUpload.items]
      : [];
    let uploadedCount = 0;
    let failedCount = 0;
    let lastErrorMessage = '';

    for (const upload of preparedUploads) {
      const formData = new FormData();
      const remoteUploadsPayload: Array<{
        source: string;
        type: string;
        remoteKey?: string;
        description?: string;
      }> = existingItems
        .filter((item: any) => typeof item?.source === 'string' && item.source)
        .map((item: any) => ({
          source: item.source,
          type: (item.type || inferUploadTypeFromSource(item.source) || 'application/octet-stream')
            .toString()
            .toLowerCase(),
          description: item.description || '',
        }));

      const hasRemoteUpload =
        (upload.remoteUploaded && (upload.remoteSource || upload.remoteKey)) ||
        (!!upload.remoteSource && upload.isPdf);

      if (hasRemoteUpload) {
        const remoteSource =
          (typeof upload.remoteSource === 'string' && upload.remoteSource.length > 0
            ? upload.remoteSource
            : typeof upload.remoteKey === 'string'
              ? `/${upload.remoteKey}`
              : '') || '';

        if (remoteSource) {
          remoteUploadsPayload.push({
            source: remoteSource,
            type: upload.fileType || upload.mimeType || upload.type || 'application/pdf',
            remoteKey: upload.remoteKey,
            description: '',
          });
        }
      } else {
        if (!upload.blobToSend) {
          console.error('No blob to send');
          failedCount += 1;
          continue;
        }

        const croppedFile = new File([upload.blobToSend], upload.name, {
          type: upload.type,
          lastModified: Date.now(),
        });
        formData.append('files', croppedFile);
        formData.append('descriptions', '');
      }

      if (remoteUploadsPayload.length > 0) {
        formData.append('remoteUploads', JSON.stringify(remoteUploadsPayload));
      }

      formData.append('accountId', accountId.toString());
      formData.append('fileStoreUrl', Config.fileStoreUrl);
      formData.append('preserveFileName', 'true');

      try {
        let uploadResponse: any;
        if (uploadRecordId) {
          formData.append('id', uploadRecordId.toString());
          uploadResponse = await editDraperyOrderUpload(formData);
        } else {
          uploadResponse = await addDraperyOrderUpload(formData);
          if (uploadResponse?.id) {
            uploadRecordId = uploadResponse.id;
          }
        }

        const updatedItems = Array.isArray(uploadResponse?.items) ? uploadResponse.items : [];
        if (updatedItems.length > 0) {
          existingItems = updatedItems;
        }
        uploadedCount += 1;
      } catch (error) {
        failedCount += 1;
        lastErrorMessage = isPurchasedGoodsOrder
          ? 'Failed to upload one or more PO documents.'
          : 'Failed to upload one or more files.';
        console.error('Failed to upload file:', error);
      }
    }

    if (uploadedCount > 0) {
      const uploadLabel = isPurchasedGoodsOrder ? 'document' : 'file';
      const description =
        uploadedCount === 1
          ? uploadSuccessToast.description
          : `${uploadedCount} ${uploadLabel}${uploadedCount > 1 ? 's' : ''} uploaded successfully.`;
      toast(uploadSuccessToast.title, {
        description,
      });
      if (handleTriggerProjectRefetch) handleTriggerProjectRefetch();
    }

    if (failedCount === 0 && uploadedCount > 0) {
      setActiveDropdown(null);
    } else if (failedCount > 0) {
      const errorMessage =
        lastErrorMessage ||
        (isPurchasedGoodsOrder
          ? 'Failed to upload PO document. Please try again.'
          : 'Failed to upload file. Please try again.');
      setUploadError(errorMessage);
      toast.error('Upload failed', {
        description:
          uploadedCount > 0 ? `${uploadedCount} uploaded, ${failedCount} failed.` : errorMessage,
      });
    }
  };

  const handleDeleteUpload = async (item: any) => {
    if (!canEditOrder) return;
    const arrayToPayload = [...(data?.draperyOrderUpload?.items || [])];
    const filtered = arrayToPayload.filter((i) => i.source !== item.source);
    const payload = {
      id: data?.draperyOrderUpload?.id,
      items: filtered,
    };
    try {
      await deleteDraperyOrderUpload(payload);
      toast(deleteSuccessToast.title, {
        description: deleteSuccessToast.description,
      });
      if (handleTriggerProjectRefetch) handleTriggerProjectRefetch();
    } catch (error) {
      console.error('Failed to delete file:', error);
      const message = isPurchasedGoodsOrder
        ? 'Failed to delete PO document. Please try again.'
        : 'Failed to delete file. Please try again.';
      toast.error('Delete failed', {
        description: message,
      });
    }
  };

  const changeInvoicePaid = (value: string) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, invoicePaid: value };
    onEdited([updatedProject]);
    setActiveDropdown(null);
  };

  const changeInvoiceDateSent = (value: Date | null) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, invoiceDateSent: value };
    onEdited([updatedProject]);
    setInvoiceDateOpen(false);
  };

  const changeInvoiceNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, invoiceNumber: e.currentTarget.value };
    onEdited([updatedProject]);
  };

  const changeInstallNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, installNumber: e.currentTarget.value };
    onEdited([updatedProject]);
  };

  const changeProductionDate = (
    value: Date | null,
    field: 'productionStartDate' | 'productionFinishedDate'
  ) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, [field]: value };
    onEdited([updatedProject]);
    setOpenDateField(null);
  };

  const changeInstallDate = (value: Date | null) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, installDate: value };
    onEdited([updatedProject]);
    setOpenDateField(null);
  };

  const changeEstimateStatus = (value: string) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, estimateStatus: value };
    onEdited([updatedProject]);
    setActiveDropdown(null);
  };

  const changeEstimateDateSent = (value: Date | null) => {
    if (!canEditOrder || !onEdited) return;
    const updatedProject = { ...data, estimateDateSent: value, estimateDate: value };
    onEdited([updatedProject]);
    setEstimateDateOpen(false);
  };

  const changeEstimateNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditOrder || !onEdited) return;
    if (hasEstimateConnection) return;
    const updatedProject = { ...data, qbEstimateNumber: e.currentTarget.value };
    onEdited([updatedProject]);
  };

  const handleUpdateSummaryComponents = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    componentId: any
  ) => {
    if (!canEditOrder) return;
    const RE = /^(?:$|(?:0|[1-9]\d{0,2})(?:[.,]\d{0,2})?)$/;
    const { name, value } = e.target;
    if ((name === 'quantity' && RE.test(value)) || name !== 'quantity') {
      setProjectComponents((prev: any) =>
        prev.map((comp: any) => {
          const next = { ...comp, [name]: value };
          if (name === 'quantity') {
            next['qty'] = value;
          }
          return comp.id === componentId ? next : comp;
        })
      );
    }
  };

  const handleUpdateSummaryComponentsDates = (
    value: Date | null,
    componentId: any,
    name: string
  ) => {
    if (!canEditOrder) return;
    const newDate = value ? new Date(value) : null;
    setProjectComponents((prev: any) =>
      prev.map((comp: any) => (comp.id === componentId ? { ...comp, [name]: newDate } : comp))
    );
  };

  const handleSaveNewSummaryComponents = async () => {
    if (!canEditOrder) return;
    const newProject = { ...data };

    if (data?.orderType === 'purchased-goods') {
      let payload = {
        id: data?.id,
        purchasedGoodsComponents: projectComponents,
      };

      if (data?.purchasedGoodsComponentsType === 'components-build') {
        newProject.purchasedGoodsComponentsBuild = projectComponents;
        payload['type'] = 'components-build';
      } else {
        newProject.purchasedGoodsComponents = projectComponents;
        payload['type'] = 'components-total';
      }

      const res = await componentUpdatePurchasedGood(payload);
      if (res && handleTriggerProjectRefetch) handleTriggerProjectRefetch();
    } else {
      newProject.components = projectComponents;
      if (handleSaveComponents) handleSaveComponents(projectComponents);
    }
    if (onEdited) onEdited([newProject]);
    handleOpenComponents?.(-1);
  };

  const handleRowCheck = (checked: boolean) => {
    if (!onCheckRow || rowId === -1) return;
    onCheckRow(rowId, checked);
  };

  const handleItemClick = () => {
    if (!canOpenOrder || isEditDisabled) return;
    const clientIdRaw =
      data?.clientId ??
      data?.client?.id ??
      data?.client?.clientId ??
      (typeof data?.client === 'object' ? data?.client?.value : undefined);
    const itemIdRaw = data?.itemId ?? data?.id ?? (rowId !== -1 ? rowId : undefined);
    const clientId =
      typeof clientIdRaw === 'number'
        ? clientIdRaw
        : typeof clientIdRaw === 'string' && clientIdRaw.trim() !== ''
          ? Number(clientIdRaw)
          : undefined;
    const itemId =
      typeof itemIdRaw === 'number'
        ? itemIdRaw
        : typeof itemIdRaw === 'string' && itemIdRaw.trim() !== ''
          ? Number(itemIdRaw)
          : undefined;
    if (data?.orderType === 'purchased-goods') {
      handleOpenSinglePurchasedGoods?.(data?.id ?? rowId);
      return;
    }
    const clientDisplayName =
      data?.client?.displayName ||
      data?.client?.customDisplayName ||
      data?.clientName ||
      clientName;
    onItemClick?.(clientId, itemId, 'summary', clientDisplayName, data?.orderType);
  };

  const isDuplicateBlocked =
    data?.orderType === 'roman-shade-order' &&
    data?.newRomanShade !== 1 &&
    ((Array.isArray(data?.components) &&
      data?.components.some((entry: any) => entry?.item?.trim() === 'TRIM FABRIC')) ||
      hasHardwareTabValues(data?.draperyOrderHardware, data));

  const hardware_has_any_row =
    data?.draperyOrderHardware?.rods?.some(
      (r: any) => r?.finish || r?.quantity || r?.description
    ) ||
    data?.draperyOrderHardware?.otherItems?.some(
      (o: any) => o?.finish || o?.quantity || o?.description
    );
  const headrail_has_any_row =
    data?.draperyOrderHeadrail?.rods?.some(
      (r: any) => r?.finish || r?.quantity || r?.description
    ) ||
    data?.draperyOrderHeadrail?.otherItems?.some(
      (o: any) => o?.finish || o?.quantity || o?.description
    );

  return (
    <>
      <TableRow
        className={cn(
          'border-b border-border/60 project-overview-row',
          data?.archived
            ? 'archived-row !bg-[#f3f4f6] hover:!bg-[#eceff3] [&>td]:!bg-[#f3f4f6] hover:[&>td]:!bg-[#eceff3]'
            : 'hover:bg-[#F8FAFC]',
          className
        )}
      >
        <TableCell className="w-10 px-0 py-0 po-td-select">
          <div className="flex items-center justify-center">
            <Checkbox
              aria-label="Select row"
              checked={isChecked}
              data-qa={rowQa('select_trigger')}
              onCheckedChange={(checked) => handleRowCheck(checked === true)}
            />
          </div>
        </TableCell>
        {showClient && showColumn('client') && (
          <TableCell className="min-w-0 align-top po-td-client">
            <div className="flex min-w-0 flex-col">
              <button
                type="button"
                className="block w-full text-left text-sm font-medium text-primary underline underline-offset-2 whitespace-normal break-words [overflow-wrap:anywhere]"
                data-qa={rowQa('client_trigger')}
                onClick={() => {
                  const clientId =
                    data?.clientId ??
                    data?.client?.id ??
                    data?.client?.clientId ??
                    (typeof data?.client === 'object' ? data?.client?.value : undefined);
                  onClientClick?.(
                    typeof clientId === 'number'
                      ? clientId
                      : typeof clientId === 'string'
                        ? Number(clientId)
                        : undefined
                  );
                }}
              >
                {clientName}
              </button>
              <span className="block w-full text-xs text-slate-500 whitespace-normal break-words [overflow-wrap:anywhere]">
                {parentChain && parentChain.length > 0
                  ? parentChain.map((parent: any, index: number) => {
                      const name = typeof parent === 'string' ? parent : getDisplayName(parent);
                      if (!name || isBooleanLike(name)) return null;
                      const parentId = typeof parent === 'object' ? parent?.id : undefined;
                      const showSeparator = !!(parent?.parentClient || parent?.parent);
                      return (
                        <button
                          key={`${name}-${index}`}
                          type="button"
                          className="inline text-left text-xs text-slate-500 hover:underline whitespace-normal break-words [overflow-wrap:anywhere]"
                          data-qa={rowQa(`parent_client_${index}_trigger`)}
                          onClick={() => onClientClick?.(parentId)}
                        >
                          {name}
                          {showSeparator ? ' / ' : ''}
                        </button>
                      );
                    })
                  : parentName || ''}
              </span>
            </div>
          </TableCell>
        )}
        {showColumn('item') && (
          <TableCell className="min-w-[130px] align-top po-td-item">
            <div className="flex min-w-0 flex-col">
              <button
                type="button"
                className={cn(
                  'block w-full whitespace-normal break-words [overflow-wrap:anywhere] text-left text-sm leading-snug',
                  canOpenOrder && !isEditDisabled
                    ? 'client_name'
                    : 'text-[#4a4a4a] cursor-default hover:no-underline'
                )}
                data-qa={rowQa('item_trigger')}
                onClick={handleItemClick}
                disabled={!canOpenOrder || isEditDisabled}
                title={itemDisplayName}
              >
                {itemDisplayName}
              </button>
              <span className="mt-0.5 block w-full text-xs text-slate-500 whitespace-normal break-words [overflow-wrap:anywhere]">
                {roomName}
              </span>
            </div>
          </TableCell>
        )}
        {showColumn('status') && (
          <TableCell className="relative inlineStatus min-w-[180px] w-[180px] max-w-[180px] whitespace-normal text-sm font-medium leading-snug text-slate-700 po-td-status">
            <div ref={statusContainerRef}>
              <HoverableItem
                dataQa={rowQa('status_trigger')}
                className={`${!data?.archived && canEditOrder ? 'editInlineField' : 'archivedColor'} leftAlign status-wrap-span`}
                handleSetRowId={() => {
                  if (data?.archived || !canEditOrder) return;
                  setActiveDropdown((prev) => (prev === 'status' ? null : 'status'));
                }}
              >
                <span className="status-line">
                  <span className={handleCellColor(statusName)}>{statusName || ''}</span>
                  {!data?.archived && canEditOrder && (
                    <ChevronDown
                      size={16}
                      className={cn('ml-1 h-4 w-4 shrink-0', handleCellColor(statusName))}
                    />
                  )}
                </span>
                {data?.archived && (
                  <div
                    style={{
                      fontSize: '12px',
                      fontStyle: 'italic',
                      textAlign: 'left',
                    }}
                  >
                    {'(archived)'}
                  </div>
                )}
              </HoverableItem>

              {statusOpen &&
                !data?.archived &&
                canEditOrder &&
                dropdownStyle &&
                typeof document !== 'undefined' &&
                createPortal(
                  <ul
                    ref={statusDropdownRef}
                    className="inlineStatusDropdown"
                    style={dropdownStyle ?? undefined}
                  >
                    <li
                      className="new_status"
                      data-qa={rowQa('status_create_new_status_trigger')}
                      onClick={() => handleSetNewStatus()}
                    >
                      <div>
                        <PlusIcon className="plus_icon" />
                      </div>
                      <div>
                        <span>New Status</span>
                      </div>
                    </li>
                    {newStatus && (
                      <li
                        className="flex items-center justify-between cursor-auto"
                        style={{ gap: '15px' }}
                      >
                        <div className="new_status_input_container">
                          <Input
                            ref={newStatusRef}
                            value={newStatusValue}
                            onChange={handleChangeNewStatus}
                            data-qa={rowQa('status_create_status_input')}
                            placeholder="Enter Name..."
                            className="input"
                          />
                        </div>

                        {newStatusValue && (
                          <div className="new_status_save_btn_container">
                            <button
                              type="button"
                              data-qa={rowQa('status_create_status_save_trigger')}
                              onClick={() => handleSaveNewStatus()}
                            >
                              Save
                            </button>
                          </div>
                        )}
                      </li>
                    )}
                    <div className="ul_body">
                      {(tableStatuses || []).map((ts: any, idx: number) => {
                        const optionValue = ts?.value ?? ts?.name ?? ts;
                        const optionLabel = ts?.name ?? ts?.value ?? ts;
                        if (!optionValue) return null;
                        const isDefault = initial_statuses
                          .map((s: any) => s.value)
                          .includes(optionValue);
                        return (
                          <li
                            key={`${optionValue}-${idx}`}
                            className={`${handleCellColor(optionValue)} ${
                              isDefault ? 'default-status' : 'custom-status'
                            }`}
                            data-qa={rowQa(`status_item_${qaToken(optionValue)}`)}
                            onClick={() => handleChangeStatus(optionValue)}
                          >
                            {optionLabel}
                          </li>
                        );
                      })}
                    </div>
                  </ul>,
                  document.body
                )}
            </div>
          </TableCell>
        )}
        {showColumn('notebook') && (
          <TableCell className="min-w-[92px] text-center text-slate-600 relative overflow-visible po-td-notes">
            <div className="w-full text-center">
              <Notebook
                row={data}
                defaultTab="items"
                notebookTabs={['projects', 'items']}
                user={accountUser}
                table={'items'}
                canAccessTeamSettings={canAccessTeamSettings}
                updateNotebook={updateNotebook}
                closeOnOutsideClick={false}
              />
              {/* <NoteCell
              row={noteRow}
              handleNotes={handleNotes}
              isNewNote={notesNewModal === noteRow?.original?.id}
              handleNewNote3DotMenu={handleNewNote3DotMenu}
              disabled={!canEditOrder}
            /> */}
            </div>
          </TableCell>
        )}
        {showColumn('wopo') && (
          <TableCell className="min-w-0 text-center po-td-wopo">
            <div className="flex items-center justify-center">
              <span
                data-draft={isDraft}
                className={`font-medium ${onWorkOrderClick && workOrder ? 'cursor-pointer underline underline-offset-2' : ''} data-[draft=true]:text-pink data-[draft=false]:text-primary`}
                data-qa={rowQa('work_order_trigger')}
                onClick={() =>
                  onWorkOrderClick && rowId !== -1 && workOrder && onWorkOrderClick(rowId)
                }
              >
                {workOrder}
              </span>
            </div>
          </TableCell>
        )}
        {showColumn('uploads') && (
          <TableCell className="min-w-0 text-center relative overflow-visible po-td-uploads">
            <div className="flex items-center justify-center">
              <button
                ref={uploadsButtonRef}
                onClick={() => canEditOrder && setActiveDropdown('uploads')}
                type="button"
                data-qa={rowQa(isPurchasedGoodsOrder ? 'po_documents_trigger' : 'uploads_trigger')}
                disabled={!canEditOrder}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none hover:bg-accent hover:text-accent-foreground rounded-md h-8 w-8 p-0"
              >
                {uploads.length > 0 ? (
                  <DocumentIcon className="cursor-pointer text-primary" width={20} height={20} />
                ) : (
                  <UploadIcon className="cursor-pointer" width={20} height={20} />
                )}
              </button>
            </div>
            {uploadsOpen &&
              dropdownStyle &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={uploadRef}
                  style={dropdownStyle ?? undefined}
                  className="flex flex-col bg-white rounded-[10px] text-[#4a4a4a] text-[14px]"
                >
                  <div className="cursor-auto rounded-md border border-input bg-popover text-popover-foreground shadow-md outline-none pointer-events-auto w-80 p-3">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm m-0">{uploadsTitle}</h4>
                        <button
                          onClick={() => setActiveDropdown(null)}
                          ref={closeUploadsRef}
                          type="button"
                          data-qa={rowQa(
                            isPurchasedGoodsOrder
                              ? 'po_documents_close_trigger'
                              : 'uploads_close_trigger'
                          )}
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none hover:bg-accent hover:text-accent-foreground rounded-md h-6 w-6 p-0"
                        >
                          <IoMdClose width={20} height={20} />
                        </button>
                      </div>

                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {uploads.length > 0 &&
                          uploads.map((i: any, idx: number) => {
                            const fileNamePopup = i?.source
                              ? i.source.substring(i.source.lastIndexOf('/') + 1)
                              : '';
                            return (
                              <div
                                key={idx}
                                className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded-md"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <DocumentIcon
                                    className="text-muted-foreground shrink-0"
                                    width={20}
                                    height={20}
                                  />
                                  <span className="text-sm truncate">{fileNamePopup}</span>
                                </div>

                                <div className="flex items-center gap-1 shrink-0">
                                  <button
                                    onClick={async () => {
                                      const tempUrl = await getTempUrl(i);
                                      window.open(tempUrl, '_blank');
                                    }}
                                    data-qa={rowQa(`${uploadDocumentQaPrefix}_${idx}_open_trigger`)}
                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 hover:bg-accent rounded-md h-6 w-6 p-0"
                                  >
                                    <ShareIcon
                                      className="text-muted-foreground hover:text-foreground transition"
                                      width={18}
                                      height={18}
                                    />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUpload(i)}
                                    data-qa={rowQa(
                                      `${uploadDocumentQaPrefix}_${idx}_delete_trigger`
                                    )}
                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 hover:bg-accent rounded-md h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                  >
                                    <TrashCanDynamic
                                      className="text-muted-foreground hover:text-destructive transition"
                                      width={20}
                                      height={20}
                                    />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>

                      {uploadError && (
                        <div className="mt-3 w-full">
                          <UploadErrorBanner centered message={uploadError} />
                        </div>
                      )}

                      <div className="text-xs text-muted-foreground text-center">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          multiple
                          style={{ display: 'none' }}
                          onChange={handleChangeFile}
                        />

                        <button
                          onClick={handleChooseFile}
                          data-qa={rowQa(
                            isPurchasedGoodsOrder
                              ? 'po_document_upload_trigger'
                              : 'upload_document_upload_trigger'
                          )}
                          className="text-black inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none border border-input bg-background hover:border-input-hover h-9 rounded-md px-3 w-full"
                          type="button"
                        >
                          <UploadIcon width={20} height={20} />
                          {uploadButtonLabel}
                        </button>
                      </div>

                      {uploads.length <= 0 && (
                        <div className="text-xs text-muted-foreground text-center">
                          {uploadsEmptyLabel}
                        </div>
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )}
          </TableCell>
        )}
        {showColumn('estimate') && (
          <TableCell className="inlineEstimateStatus min-w-[196px] po-td-estimate">
            <div className="flex flex-col w-full">
              <div ref={estimateStatusTriggerRef}>
                <HoverableItem
                  dataQa={rowQa('estimate_status_trigger')}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setActiveDropdown((prev) =>
                      prev === 'estimateStatus' ? null : 'estimateStatus'
                    );
                  }}
                  className={`${canEditOrder ? 'editInlineField' : ''} paddings leftAlign ${
                    data?.archived ? 'archivedColor' : ''
                  } status-wrap-span`}
                >
                  <span className="status-line">
                    <span className={getEstimateStatusClass(estimateStatusValue || 'Not Created')}>
                      {estimateStatusValue ? estimateStatusValue : 'Not Created'}
                    </span>
                    {!data?.archived && canEditOrder && (
                      <ChevronDown
                        size={16}
                        className={cn(
                          'ml-1 h-4 w-4 shrink-0',
                          getEstimateStatusClass(estimateStatusValue || 'Not Created')
                        )}
                      />
                    )}
                  </span>
                </HoverableItem>
              </div>

              {estimateStatusOpen &&
                !data?.archived &&
                canEditOrder &&
                dropdownStyle &&
                typeof document !== 'undefined' &&
                createPortal(
                  <ul
                    ref={estimateStatusRef}
                    className="inlineEstimateStatusDropdown"
                    style={dropdownStyle ?? undefined}
                  >
                    <li
                      className="estimate_status_not_created"
                      data-qa={rowQa('estimate_status_item_not_created')}
                      onClick={() => changeEstimateStatus('Not Created')}
                    >
                      Not Created
                    </li>
                    <li
                      className="estimate_status_created"
                      data-qa={rowQa('estimate_status_item_created')}
                      onClick={() => changeEstimateStatus('Created')}
                    >
                      Created
                    </li>
                    <li
                      className="estimate_status_exported"
                      data-qa={rowQa('estimate_status_item_exported_to_qb')}
                      onClick={() => changeEstimateStatus('Exported to QB')}
                    >
                      Exported to QB
                    </li>
                    <li
                      className="estimate_status_sent"
                      data-qa={rowQa('estimate_status_item_sent_to_customer')}
                      onClick={() => changeEstimateStatus('Sent to Customer')}
                    >
                      Sent to Customer
                    </li>
                  </ul>,
                  document.body
                )}
              {estimateNumberOpen && canEditEstimateNumber ? (
                <Input
                  placeholder="#"
                  type="text"
                  data-qa={rowQa('estimate_number_input')}
                  value={estimateNumberValue || ''}
                  onChange={changeEstimateNumber}
                  onBlur={() => setEstimateNumberOpen(false)}
                  className="po-inline-number-input"
                  autoFocus
                />
              ) : hasEstimateConnection ? (
                <div
                  className={`paddingLeft leftAlign ${data?.archived ? 'archivedColor' : ''}`}
                  data-qa={rowQa('estimate_number_trigger')}
                >
                  <EstimateStatusCell
                    summary={data?.estimateStatusSummary}
                    status={estimateStatusValue || 'Not Created'}
                    date={estimateStatusDate}
                    estimateId={data?.estimateId}
                    estimateNumber={data?.estimateNumber}
                    qbEstimateId={data?.qbEstimateId}
                    qbEstimateNumber={data?.qbEstimateNumber}
                    canOpenEstimate={hasPricingPermission && !!onOpenEstimate}
                    onOpenEstimate={
                      onOpenEstimate
                        ? (estimateId, estimate) =>
                            onOpenEstimate(estimateId, {
                              ...estimate,
                              client: data?.client || client || null,
                            })
                        : undefined
                    }
                    getStatusClassName={getEstimateStatusClass}
                    compact
                    showStatusLine={false}
                    dataQaPrefix={rowQa('estimate')}
                  />
                </div>
              ) : (
                <HoverableItem
                  dataQa={rowQa('estimate_number_trigger')}
                  className={`paddingLeft leftAlign ${
                    canEditEstimateNumber ? 'editInlineField' : ''
                  } ${data?.archived ? 'archivedColor' : ''}`}
                  handleSetRowId={() => {
                    if (!canEditEstimateNumber) return;
                    setEstimateNumberOpen(true);
                  }}
                >
                  {estimateNumberValue ? (
                    `#${estimateNumberValue}`
                  ) : (
                    <span className={!data?.archived && canEditEstimateNumber ? 'hoverInline' : ''}>
                      #_
                    </span>
                  )}
                </HoverableItem>
              )}

              {estimateDateOpen && !data?.archived && canEditOrder ? (
                <div
                  ref={estimateDateRef}
                  style={{ width: '100%' }}
                  className="inlinePosition flex-wrap"
                >
                  <Popover
                    open={estimateDateOpen}
                    onOpenChange={(open) => !open && setEstimateDateOpen(false)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-qa={rowQa('estimate_date_picker_trigger')}
                        className={inlineDateTriggerClassName}
                      >
                        {estimateSentDateToShow || 'MM/DD/YY'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0"
                      onOpenAutoFocus={(event) => event.preventDefault()}
                    >
                      <Calendar
                        mode="single"
                        selected={toCalendarDate(estimateStatusDate)}
                        className="rounded-md border-0 p-[12px]"
                        onSelect={(v) => changeEstimateDateSent(v || null)}
                      />
                      {!!estimateSentDateToShow && (
                        <div className="border-t px-3 py-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-rose-600"
                            onClick={() => changeEstimateDateSent(null)}
                          >
                            <X className="h-3 w-3" />
                            Clear
                          </button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <div
                  data-qa={rowQa('estimate_date_trigger')}
                  role="button"
                  tabIndex={data?.archived || !canEditOrder ? -1 : 0}
                  className={`group/date inline-flex items-center gap-1 editInlineField leftAlign status-wrap-span ${
                    data?.archived ? 'archivedColor' : ''
                  }`}
                  onClick={() => {
                    if (data?.archived || !canEditOrder) return;
                    setEstimateDateOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (data?.archived || !canEditOrder) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setEstimateDateOpen(true);
                    }
                  }}
                >
                  {estimateSentDateToShow ? (
                    <span>{estimateSentDateToShow}</span>
                  ) : (
                    <span className={!data?.archived && canEditOrder ? 'hoverInline' : ''}>
                      MM/DD/YY
                    </span>
                  )}
                  {!!estimateSentDateToShow && !data?.archived && canEditOrder && (
                    <button
                      type="button"
                      data-qa={rowQa('estimate_date_clear_trigger')}
                      aria-label="Clear estimate date"
                      className={dateClearButtonClassName}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        changeEstimateDateSent(null);
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </TableCell>
        )}
        {showColumn('invoice') && (
          <TableCell className="inlinePaid min-w-[160px] po-td-invoice">
            <div className="flex flex-col w-full">
              <div ref={invoicePaidTriggerRef}>
                <HoverableItem
                  dataQa={rowQa('invoice_status_trigger')}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setActiveDropdown((prev) => (prev === 'invoicePaid' ? null : 'invoicePaid'));
                  }}
                  className={`${canEditOrder ? 'editInlineField' : ''} paddings leftAlign ${
                    data?.archived ? 'archivedColor' : ''
                  } status-wrap-span`}
                >
                  <span className="status-line">
                    <span
                      style={{ fontSize: '14px' }}
                      className={getInvoiceStatusClass(invoiceStatusValue)}
                    >
                      {invoiceStatusValue}
                    </span>
                    {!data?.archived && canEditOrder && (
                      <ChevronDown
                        size={16}
                        className={cn(
                          'ml-1 h-4 w-4 shrink-0',
                          getInvoiceStatusClass(invoiceStatusValue)
                        )}
                      />
                    )}
                  </span>
                </HoverableItem>
              </div>

              {invoicePaidOpen &&
                !data?.archived &&
                canEditOrder &&
                dropdownStyle &&
                typeof document !== 'undefined' &&
                createPortal(
                  <ul
                    ref={invoicePaidRef}
                    className="inlinePaidDropdown"
                    style={dropdownStyle ?? undefined}
                  >
                    <li
                      className="invoicePaid_not_created"
                      data-qa={rowQa('invoice_status_item_not_created')}
                      onClick={() => changeInvoicePaid('Not Created')}
                    >
                      Not Created
                    </li>
                    <li
                      className="invoicePaid_paid"
                      data-qa={rowQa('invoice_status_item_paid')}
                      onClick={() => changeInvoicePaid('Paid')}
                    >
                      Paid
                    </li>
                    <li
                      className="invoicePaid_partial"
                      data-qa={rowQa('invoice_status_item_partial')}
                      onClick={() => changeInvoicePaid('Partial')}
                    >
                      Partial
                    </li>
                    <li
                      className="invoicePaid_not_paid"
                      data-qa={rowQa('invoice_status_item_not_paid')}
                      onClick={() => changeInvoicePaid('Not Paid')}
                    >
                      Not Paid
                    </li>
                  </ul>,
                  document.body
                )}

              {invoiceNumberOpen && !data?.archived ? (
                <Input
                  placeholder="#"
                  type="text"
                  data-qa={rowQa('invoice_number_input')}
                  value={data?.invoiceNumber || ''}
                  onChange={changeInvoiceNumber}
                  onBlur={() => setInvoiceNumberOpen(false)}
                  className="po-inline-number-input"
                  autoFocus
                />
              ) : (
                <HoverableItem
                  dataQa={rowQa('invoice_number_trigger')}
                  className={`paddingLeft leftAlign ${data?.archived ? 'archivedColor' : ''}`}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setInvoiceNumberOpen(true);
                  }}
                >
                  {data?.invoiceNumber ? (
                    `#${data?.invoiceNumber}`
                  ) : (
                    <span className={!data?.archived && canEditOrder ? 'hoverInline' : ''}>#_</span>
                  )}
                </HoverableItem>
              )}

              {invoiceDateOpen && !data?.archived && canEditOrder ? (
                <div
                  ref={invoiceDateRef}
                  style={{ width: '100%' }}
                  className="inlinePosition flex-wrap"
                >
                  <Popover
                    open={invoiceDateOpen}
                    onOpenChange={(open) => !open && setInvoiceDateOpen(false)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-qa={rowQa('invoice_date_picker_trigger')}
                        className={inlineDateTriggerClassName}
                      >
                        {invoiceSentDateToShow || 'MM/DD/YY'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0"
                      onOpenAutoFocus={(event) => event.preventDefault()}
                    >
                      <Calendar
                        mode="single"
                        selected={toCalendarDate(data?.invoiceDateSent)}
                        className="rounded-md border-0 p-[12px]"
                        onSelect={(v) => changeInvoiceDateSent(v || null)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <HoverableItem
                  dataQa={rowQa('invoice_date_trigger')}
                  className={`editInlineField leftAlign status-wrap-span ${
                    data?.archived ? 'archivedColor' : ''
                  }`}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setInvoiceDateOpen(true);
                  }}
                >
                  <span className="group/date inline-flex items-center gap-1">
                    {invoiceSentDateToShow ? (
                      <span>{invoiceSentDateToShow}</span>
                    ) : (
                      <span className={!data?.archived && canEditOrder ? 'hoverInline' : ''}>
                        MM/DD/YY
                      </span>
                    )}
                    {!!invoiceSentDateToShow && !data?.archived && canEditOrder && (
                      <button
                        type="button"
                        data-qa={rowQa('invoice_date_clear_trigger')}
                        aria-label="Clear invoice date"
                        className={dateClearButtonClassName}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          changeInvoiceDateSent(null);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </HoverableItem>
              )}
            </div>
          </TableCell>
        )}
        {showColumn('components') && (
          <TableCell className="min-w-0 text-center po-td-components">
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => canEditOrder && handleOpenComponents?.(rowId)}
                data-qa={rowQa('components_ordered_trigger')}
                className={cn(
                  'font-medium underline underline-offset-2',
                  !canEditOrder && 'cursor-default no-underline',
                  getBooleanTextClass(componentsOrderedData ? 'Yes' : 'No')
                )}
              >
                {componentsOrderedData ? 'Yes' : 'No'}
              </button>
              <span className="text-muted-foreground">/</span>
              <button
                type="button"
                onClick={() => canEditOrder && handleOpenComponents?.(rowId)}
                data-qa={rowQa('components_received_trigger')}
                className={cn(
                  'font-medium underline underline-offset-2',
                  !canEditOrder && 'cursor-default no-underline',
                  getBooleanTextClass(componentsReceivedData ? 'Yes' : 'No')
                )}
              >
                {componentsReceivedData ? 'Yes' : 'No'}
              </button>
            </div>
          </TableCell>
        )}
        {showColumn('production') && (
          <TableCell className="min-w-0 po-td-production">
            <div className="flex flex-col">
              {openDateField === 'productionStartDate' && !data?.archived && canEditOrder ? (
                <div style={{ width: '100%' }} className="inlinePosition">
                  <Popover
                    open={openDateField === 'productionStartDate'}
                    onOpenChange={(open) => !open && setOpenDateField(null)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-qa={rowQa('production_start_date_picker_trigger')}
                        className={inlineDateTriggerClassName}
                      >
                        {productionStart}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0"
                      onOpenAutoFocus={(event) => event.preventDefault()}
                    >
                      <Calendar
                        mode="single"
                        selected={toCalendarDate(data?.productionStartDate)}
                        className="rounded-md border-0 p-[12px]"
                        onSelect={(v) => changeProductionDate(v || null, 'productionStartDate')}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <HoverableItem
                  dataQa={rowQa('production_start_date_trigger')}
                  className={`${!data?.archived && canEditOrder ? 'editInlineField' : 'archivedColor'} leftAlign`}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setOpenDateField('productionStartDate');
                  }}
                >
                  <span className="group/date inline-flex items-center gap-1">
                    <span
                      className={isProductionStartPlaceholder ? 'text-muted-foreground' : undefined}
                    >
                      {productionStart}
                    </span>
                    {!isProductionStartPlaceholder && !data?.archived && canEditOrder && (
                      <button
                        type="button"
                        data-qa={rowQa('production_start_date_clear_trigger')}
                        aria-label="Clear production start date"
                        className={dateClearButtonClassName}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          changeProductionDate(null, 'productionStartDate');
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </HoverableItem>
              )}
              {openDateField === 'productionFinishedDate' && !data?.archived && canEditOrder ? (
                <div style={{ width: '100%' }} className="inlinePosition">
                  <Popover
                    open={openDateField === 'productionFinishedDate'}
                    onOpenChange={(open) => !open && setOpenDateField(null)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-qa={rowQa('production_finish_date_picker_trigger')}
                        className={inlineDateTriggerClassName}
                      >
                        {productionFinish}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0"
                      onOpenAutoFocus={(event) => event.preventDefault()}
                    >
                      <Calendar
                        mode="single"
                        selected={toCalendarDate(data?.productionFinishedDate)}
                        className="rounded-md border-0 p-[12px]"
                        onSelect={(v) => changeProductionDate(v || null, 'productionFinishedDate')}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <HoverableItem
                  dataQa={rowQa('production_finish_date_trigger')}
                  className={`${!data?.archived && canEditOrder ? 'editInlineField' : 'archivedColor'} leftAlign`}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setOpenDateField('productionFinishedDate');
                  }}
                >
                  <span className="group/date inline-flex items-center gap-1">
                    <span
                      className={
                        isProductionFinishPlaceholder ? 'text-muted-foreground' : undefined
                      }
                    >
                      {productionFinish}
                    </span>
                    {!isProductionFinishPlaceholder && !data?.archived && canEditOrder && (
                      <button
                        type="button"
                        data-qa={rowQa('production_finish_date_clear_trigger')}
                        aria-label="Clear production finish date"
                        className={dateClearButtonClassName}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          changeProductionDate(null, 'productionFinishedDate');
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </HoverableItem>
              )}
            </div>
          </TableCell>
        )}
        {showColumn('install') && (
          <TableCell className="min-w-0 po-td-install">
            <div className="flex flex-col">
              {installNumberOpen && !data?.archived && canEditOrder ? (
                <Input
                  placeholder="#"
                  type="text"
                  data-qa={rowQa('install_number_input')}
                  value={installNumberText || ''}
                  onChange={changeInstallNumber}
                  onBlur={() => setInstallNumberOpen(false)}
                  className="po-inline-number-input"
                  autoFocus
                />
              ) : (
                <HoverableItem
                  dataQa={rowQa('install_number_trigger')}
                  className={`paddingLeft leftAlign ${
                    !data?.archived && canEditOrder ? 'editInlineField' : 'archivedColor'
                  }`}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setInstallNumberOpen(true);
                  }}
                >
                  {hasInstallNumber ? (
                    installNumberText
                  ) : (
                    <span className={!data?.archived && canEditOrder ? 'hoverInline' : ''}>#</span>
                  )}
                </HoverableItem>
              )}
              {openDateField === 'installDate' && !data?.archived && canEditOrder ? (
                <div style={{ width: '100%' }} className="inlinePosition">
                  <Popover
                    open={openDateField === 'installDate'}
                    onOpenChange={(open) => !open && setOpenDateField(null)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-qa={rowQa('install_date_picker_trigger')}
                        className={inlineDateTriggerClassName}
                      >
                        {installDate}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0"
                      onOpenAutoFocus={(event) => event.preventDefault()}
                    >
                      <Calendar
                        mode="single"
                        selected={toCalendarDate(data?.installDate)}
                        className="rounded-md border-0 p-[12px]"
                        onSelect={(v) => changeInstallDate(v || null)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <HoverableItem
                  dataQa={rowQa('install_date_trigger')}
                  className={`${!data?.archived && canEditOrder ? 'editInlineField' : 'archivedColor'} leftAlign`}
                  handleSetRowId={() => {
                    if (data?.archived || !canEditOrder) return;
                    setOpenDateField('installDate');
                  }}
                >
                  <span className="group/date inline-flex items-center gap-1">
                    <span
                      className={isInstallDatePlaceholder ? 'text-muted-foreground' : undefined}
                    >
                      {installDate}
                    </span>
                    {!isInstallDatePlaceholder && !data?.archived && canEditOrder && (
                      <button
                        type="button"
                        data-qa={rowQa('install_date_clear_trigger')}
                        aria-label="Clear install date"
                        className={dateClearButtonClassName}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          changeInstallDate(null);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </HoverableItem>
              )}
            </div>
          </TableCell>
        )}
        {customColumns.map((customColumn) => {
          const customColumnId = customColumn.id as CustomColumnId;
          const visibilityKey = CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID[customColumnId];
          if (!visibilityKey || !showColumn(visibilityKey)) return null;

          const currentValue = readCustomColumnValue(data?.customColumnsValues, customColumnId);
          const options = customColumn.options || [];
          const isDisabled = data?.archived || !canEditOrder || !onCustomColumnValueChange;

          if (customColumn.inputType === 'dropdown') {
            return (
              <TableCell
                key={`custom-column-${rowId}-${customColumn.id}`}
                className="min-w-0 po-td-custom px-[8px]"
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={isDisabled}>
                    <button
                      type="button"
                      className={cn(
                        'flex min-h-[34px] w-full min-w-0 items-center justify-between gap-2 rounded-[6px] border border-transparent bg-transparent px-2 py-1 text-left text-sm text-slate-600 hover:border-[#d5dde5] hover:bg-white',
                        isDisabled &&
                          'cursor-default opacity-70 hover:border-transparent hover:bg-transparent'
                      )}
                      title={currentValue || customColumn.name}
                      data-qa={rowQa(`${visibilityKey}_dropdown_trigger`)}
                      disabled={isDisabled}
                    >
                      <span
                        className={cn('block min-w-0 truncate', !currentValue && 'text-slate-400')}
                      >
                        {currentValue || 'Select'}
                      </span>
                      {!isDisabled && <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={4}
                    className="min-w-[180px] max-w-[260px] max-h-[340px] overflow-y-auto rounded-[8px] border border-[#d5dde5] bg-white p-1 shadow-[0_10px_28px_rgba(15,23,42,0.15)] z-[999]"
                  >
                    {currentValue ? (
                      <>
                        <DropdownMenuItem
                          className="text-muted-foreground italic cursor-pointer"
                          onSelect={() => handleCustomColumnChange(customColumnId, '')}
                        >
                          Clear selection
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    ) : null}
                    {options.map((option) => (
                      <DropdownMenuItem
                        key={`${customColumnId}-${option}`}
                        className="cursor-pointer text-gray-500"
                        onSelect={() => {
                          if (option === currentValue) return;
                          handleCustomColumnChange(customColumnId, option);
                        }}
                      >
                        {option}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            );
          }

          return (
            <TableCell
              key={`custom-column-${rowId}-${customColumn.id}`}
              className="min-w-0 po-td-custom px-[8px]"
            >
              {isDisabled ? (
                <span
                  className="block min-w-0 truncate text-sm text-slate-600"
                  title={currentValue}
                >
                  {currentValue}
                </span>
              ) : (
                <CustomColumnTextCell
                  value={currentValue}
                  onCommit={async (nextValue) => {
                    await onCustomColumnValueChange?.(data, customColumnId, nextValue);
                  }}
                />
              )}
            </TableCell>
          );
        })}
        {roleAssignmentColumns.map((role) => {
          const options = assignmentOptionsByRoleId[role.id] || [];
          const selectedValue = getRoleAssignmentSelectedOptionKey(roleAssignments, role.id);
          const isDisabled = data?.archived || !canEditOrder;

          return (
            <TableCell key={`role-assignment-${rowId}-${role.id}`} className="po-td-role px-[8px]">
              <RoleAssignmentDropdown
                teamMembers={roleAssignmentTeamMembers}
                assignees={roleAssignmentAssignees}
                role={role}
                value={selectedValue}
                options={options}
                disabled={isDisabled}
                isAccountAdmin={accountIsAdmin || !!accountUser?.isAdmin}
                dataQa={rowQa(`assignment_role_${role.id}_select`)}
                onChange={(nextValue) => handleRoleAssignmentChange(role.id, nextValue)}
                manageAssignments={() => {
                  if (handleOpenInvitePanel) {
                    handleOpenInvitePanel('team_member', role);
                  }
                }}
                handleUpdateTeamMember={handleUpdateTeamMember}
                handleUpdateAssignee={handleUpdateAssignee}
                handleClearValue={() => {
                  let clearedAssignments = data?.assignments?.filter(
                    (f: any) => f?.roleId !== role.id
                  );
                  if (!onEdited) return;
                  onEdited([{ ...data, assignments: clearedAssignments }]);
                }}
              />
            </TableCell>
          );
        })}
        <TableCell
          className={cn(
            'w-12 text-center relative overflow-visible po-td-actions',
            stickyCellClass
          )}
        >
          {actions ?? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  data-qa={rowQa('actions_menu_trigger')}
                >
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={!isOverviewPage ? 'w-[200px]' : 'w-[160px]'}
              >
                {!isOverviewPage && (
                  <DropdownMenuItem
                    data-qa={rowQa('actions_create_estimate_trigger')}
                    onSelect={(event) => {
                      event.preventDefault();
                      setEstimatesMovedDialogOpen(true);
                    }}
                    className="text-[#8B98AB] focus:!text-black data-[highlighted]:!text-black"
                  >
                    <FileText className="h-[18px] w-[18px] shrink-0 text-current" />
                    <span>Create Estimate</span>
                    <InfoIcon
                      data-qa={rowQa('actions_create_estimate_info')}
                      width={18}
                      height={18}
                      style={{ marginLeft: 'auto' }}
                    />
                  </DropdownMenuItem>
                )}

                {showBatchOrEditOrder ? (
                  <DropdownMenuItem
                    data-qa={rowQa('actions_batch_edit_trigger')}
                    disabled={!canUseCoreActions}
                    onSelect={() => {
                      if (!canUseCoreActions) return;
                      handleMenuAction('Batch Edit');
                      if (handleNotCheckedBatchRow && rowId !== -1) {
                        handleNotCheckedBatchRow(rowId, data);
                      }
                    }}
                  >
                    <Collection />
                    <span>Batch Edit</span>
                  </DropdownMenuItem>
                ) : (
                  hasPricingPermission && (
                    <DropdownMenuItem
                      data-qa={rowQa('actions_edit_trigger')}
                      disabled={!canOpenOrder || isEditDisabled}
                      onSelect={() => {
                        if (!canOpenOrder || isEditDisabled) return;
                        handleItemClick();
                      }}
                    >
                      {data?.archived ? <View /> : <Pencil />}
                      <span>
                        {data?.archived
                          ? 'View'
                          : data?.orderType === 'purchased-goods'
                            ? 'Edit Item'
                            : 'Edit Order'}
                      </span>
                    </DropdownMenuItem>
                  )
                )}

                {hasPricingPermission && (
                  <DropdownMenuItem
                    data-qa={rowQa('actions_duplicate_trigger')}
                    disabled={!canUsePricingActions || data?.archived}
                    className={
                      isDuplicateBlocked || !canUsePricingActions ? 'opacity-50 cursor-default' : ''
                    }
                    onSelect={() => {
                      if (!canUsePricingActions) return;
                      if (isDuplicateBlocked) {
                        onOpenModal?.('DuplicateBlocked');
                        return;
                      }
                      if (data?.archived) return;
                      if (rowId !== -1) {
                        handleRowCheck(true);
                        handleMenuAction('Duplicate', 'singleItem', [rowId]);
                      }
                    }}
                  >
                    {data?.archived || isDuplicateBlocked ? <DuplicateDisabled /> : <Duplicate />}
                    <span>Duplicate</span>
                  </DropdownMenuItem>
                )}

                {hasPricingPermission && <DropdownMenuSeparator />}

                <DropdownMenuItem
                  data-qa={rowQa('actions_archive_toggle_trigger')}
                  disabled={!canUseCoreActions}
                  onSelect={() => {
                    if (!canUseCoreActions) return;
                    if (rowId === -1) return;
                    handleRowCheck(true);
                    if (!data?.archived) {
                      onOpenModal?.('Archive');
                    } else {
                      handleMenuAction('Unarchive', undefined, [rowId]);
                    }
                  }}
                >
                  <Archive />
                  <span>{data?.archived ? 'Unarchive' : 'Archive'}</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  data-qa={rowQa('actions_delete_trigger')}
                  disabled={!canUseCoreActions}
                  onSelect={() => {
                    if (!canUseCoreActions) return;
                    if (rowId === -1) return;
                    handleRowCheck(true);
                    onOpenModal?.('Delete');
                  }}
                >
                  <TrashCan />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>

        {componentsModalProject &&
          typeof document !== 'undefined' &&
          createPortal(
            <>
              <div className="fixed left-[50%] top-[50%] z-[1000001] grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border border-input bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-animate-in data-[state=closed]:animate-animate-out sm:rounded-lg max-w-6xl max-h-[90vh] overflow-y-auto">
                <div className="flex flex-col text-center sm:text-left">
                  <div className="font-semibold tracking-tight text-xl">
                    <span>{itemDisplayName}</span>
                    {workOrder ? (
                      <>
                        <span>|</span>
                        <span>WO #{workOrder}</span>
                      </>
                    ) : null}
                    <span>|</span>
                    <span>
                      {clientName || ''} - {roomName || ''}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                    data-qa={rowQa('components_modal_close_trigger')}
                    onClick={() => handleOpenComponents?.(-1)}
                  >
                    <IoMdClose className="w-[16px] h-[16px]" />
                  </button>
                </div>

                <div className="max-h-[458px] overflow-y-auto rounded-[8px] h-full mt-[16px]">
                  <table className="w-full caption-bottom">
                    <thead className="">
                      <tr className="shadow-[0_1px_0_0_rgb(226,232,240)] sticky top-0 bg-background z-[1000] transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                        <th className="h-12 px-1.5 text-[14px] text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[30%]">
                          Component
                        </th>
                        <th className="h-12 px-1.5 text-[14px] text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[8%]">
                          Qty.
                        </th>
                        <th className="h-12 px-1.5 text-[14px] text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[12%]">
                          Order No.
                        </th>
                        <th className="h-12 px-1.5 text-[14px] text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[12%]">
                          Ordered
                        </th>
                        <th className="h-12 px-1.5 text-[14px] text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[12%]">
                          ETA
                        </th>
                        <th className="h-12 px-1.5 text-[14px] text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[12%]">
                          Received
                        </th>
                        <th className="h-12 px-1.5 text-[14px] text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[14%]">
                          Notebook
                        </th>
                      </tr>
                    </thead>

                    <tbody className="[&_tr:last-child]:border-0">
                      {projectComponents && projectComponents.length > 0 ? (
                        projectComponents
                          .sort((a: any, b: any) => {
                            const orderPriority = (obj: any) => {
                              if (obj?.item === 'MAIN FABRIC') return 0;
                              if (
                                (obj?.item === 'HEADRAIL' && obj?.fabric) ||
                                (obj?.item === 'HARDWARE' && obj?.isStaticHardware) ||
                                (obj?.item === 'PARTS & SUPPLIES' && obj?.isStaticHardware)
                              )
                                return 2;
                              return 1;
                            };
                            const priorityA = orderPriority(a);
                            const priorityB = orderPriority(b);
                            if (priorityA !== priorityB) return priorityA - priorityB;
                            return String(a.item || '').localeCompare(String(b.item || ''));
                          })
                          .map((c: any, idx: number) => {
                            const fabricSupplier =
                              c.fabric && c.fabric.supplier ? c.fabric.supplier.displayName : '';
                            const fabricPattern = c.fabric ? c.fabric.pattern : '';
                            const quantity =
                              c.item !== 'HEADRAIL'
                                ? c.fabric && c.fabric.yardsNeeded
                                  ? c.fabric.yardsNeeded
                                  : c.fabric && c.fabric.yardsNeeded === null
                                    ? 'TBD'
                                    : c.quantity
                                : data?.quantity
                                  ? data?.quantity
                                  : 'TBD';
                            const hardwareObj = data?.draperyOrderHardware;
                            const componentNotebookId = Number(c?.id || 0);
                            const supportsComponentNotebook =
                              Number.isInteger(componentNotebookId) && componentNotebookId > 0;
                            const notebookRowClient =
                              data?.client && typeof data?.client === 'object'
                                ? data.client
                                : { displayName: clientName || '' };
                            const notebookRowId =
                              Number(data?.itemId || 0) > 0
                                ? Number(data?.itemId)
                                : Number(data?.id || 0) > 0
                                  ? Number(data?.id)
                                  : null;
                            const isCustomStaticHardware =
                              data?.orderType === 'custom-order' &&
                              c?.isStaticHardware &&
                              ['HARDWARE', 'PARTS & SUPPLIES'].includes(
                                String(c?.item || '').toUpperCase()
                              );
                            const componentItemLabel = isCustomStaticHardware
                              ? String(hardwareObj?.hardwareItem || '').trim() || 'Parts & Supplies'
                              : c.item;
                            const componentDescriptionLabel = isCustomStaticHardware
                              ? String(hardwareObj?.hardwareDescription || '').trim()
                              : '';

                            const hasPoGenerated =
                              c?.poGenerated ||
                              c?.draperyOrderSummaryComponent?.poGenerated ||
                              false;
                            const poGenerated = c?.po || c?.draperyOrderSummaryComponent?.po || '';

                            return (
                              <Fragment key={idx}>
                                <tr className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                  <td className="text-[14px] px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                    <div className="font-medium">{componentItemLabel || ''}</div>
                                    {componentDescriptionLabel ? (
                                      <div className="text-sm text-muted-foreground">
                                        {componentDescriptionLabel}
                                      </div>
                                    ) : null}
                                    {c.draperyOrderFabricId ? (
                                      c.item === 'HEADRAIL' ? (
                                        <div className="text-sm text-muted-foreground">
                                          {c.headrail || ''}
                                        </div>
                                      ) : (
                                        <>
                                          <div className="text-sm text-muted-foreground">
                                            {fabricSupplier || 'Unspecified'}
                                          </div>
                                          <div className="text-sm text-muted-foreground">
                                            {fabricPattern || 'Unspecified'}
                                          </div>
                                        </>
                                      )
                                    ) : !isCustomStaticHardware ? (
                                      c.item === 'HARDWARE' && (
                                        <div className="text-sm text-muted-foreground">
                                          {(hardwareObj &&
                                            c.isStaticHardware &&
                                            hardwareObj.supplier) ||
                                            ''}
                                        </div>
                                      )
                                    ) : null}
                                  </td>
                                  <td className="text-[14px] px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                    {c.fabric ||
                                    c.item === 'HARDWARE' ||
                                    c.item === 'PARTS & SUPPLIES' ? (
                                      <span>{quantity}</span>
                                    ) : (
                                      <Input
                                        pattern="^\\d{1,3}(?:[.,]\\d{0,2})?$"
                                        // className="input shadow"
                                        className="min-h-[40px] selection:bg-primary selection:text-primary-foreground dark:bg-white min-w-0 shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 disabled:pointer-events-none focus-visible:border-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-28"
                                        data-qa={rowQa(
                                          `components_modal_${qaToken(c.id || idx)}_quantity_input`
                                        )}
                                        value={c.quantity || ''}
                                        name="quantity"
                                        onChange={(e) => handleUpdateSummaryComponents(e, c.id)}
                                      />
                                    )}
                                  </td>

                                  <td className="text-[14px] px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                    {hasPoGenerated ? (
                                      <span className="wrap-text">{poGenerated}</span>
                                    ) : (
                                      <Input
                                        className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-28"
                                        data-qa={rowQa(
                                          `components_modal_${qaToken(c.id || idx)}_order_no_input`
                                        )}
                                        value={c.po || ''}
                                        name="po"
                                        onChange={(e) => handleUpdateSummaryComponents(e, c.id)}
                                      />
                                    )}
                                  </td>

                                  <td className="text-[14px] px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                    {(c.fabric && c.fabric.orderOrigin === 2 && !c.orderedDate) ||
                                    c.purchaser === 2 ||
                                    (c.item === 'HARDWARE' &&
                                      hardwareObj &&
                                      c.isStaticHardware &&
                                      hardwareObj.purchaser === 2) ? (
                                      <div className="font-medium align-middle min-h-[39.42px] flex items-center">
                                        COM
                                      </div>
                                    ) : (
                                      <div className="group/date relative inline-flex items-center gap-1">
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <button
                                              type="button"
                                              data-qa={rowQa(
                                                `components_modal_${qaToken(c.id || idx)}_ordered_date_trigger`
                                              )}
                                              className={cn(
                                                'transition hover:text-primary text-left',
                                                formatDate(c.orderedDate) === 'MM/DD/YY'
                                                  ? 'text-muted-foreground'
                                                  : ''
                                              )}
                                            >
                                              {formatDate(c.orderedDate)}
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent align="end" className="w-auto p-0">
                                            <Calendar
                                              mode="single"
                                              selected={toCalendarDate(c.orderedDate)}
                                              className="rounded-md border-0 p-[12px]"
                                              onSelect={(v) =>
                                                handleUpdateSummaryComponentsDates(
                                                  v || null,
                                                  c.id,
                                                  'orderedDate'
                                                )
                                              }
                                            />
                                          </PopoverContent>
                                        </Popover>
                                        {formatDate(c.orderedDate) !== 'MM/DD/YY' && (
                                          <button
                                            type="button"
                                            data-qa={rowQa(
                                              `components_modal_${qaToken(c.id || idx)}_ordered_date_clear_trigger`
                                            )}
                                            aria-label="Clear ordered date"
                                            // className={dateClearButtonAbsoluteClassName}
                                            className={`h-3.5 w-3.5 rounded text-muted-foreground opacity-0 transition-opacity group-hover/date:opacity-100 hover:bg-muted hover:text-foreground hover:[&_svg]:text-destructive`}
                                            onMouseDown={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              handleUpdateSummaryComponentsDates(
                                                null,
                                                c.id,
                                                'orderedDate'
                                              );
                                            }}
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </td>

                                  <td className="text-[14px] px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                    <div className="group/date relative inline-flex items-center gap-1">
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button
                                            type="button"
                                            data-qa={rowQa(
                                              `components_modal_${qaToken(c.id || idx)}_eta_date_trigger`
                                            )}
                                            className={cn(
                                              'transition hover:text-primary text-left',
                                              formatDate(c.eta) === 'MM/DD/YY'
                                                ? 'text-muted-foreground'
                                                : ''
                                            )}
                                          >
                                            {formatDate(c.eta)}
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent align="end" className="w-auto p-0">
                                          <Calendar
                                            mode="single"
                                            selected={toCalendarDate(c.eta)}
                                            className="rounded-md border-0 p-[12px]"
                                            onSelect={(v) =>
                                              handleUpdateSummaryComponentsDates(
                                                v || null,
                                                c.id,
                                                'eta'
                                              )
                                            }
                                          />
                                        </PopoverContent>
                                      </Popover>
                                      {formatDate(c.eta) !== 'MM/DD/YY' && (
                                        <button
                                          type="button"
                                          data-qa={rowQa(
                                            `components_modal_${qaToken(c.id || idx)}_eta_date_clear_trigger`
                                          )}
                                          aria-label="Clear eta date"
                                          // className={dateClearButtonAbsoluteClassName}
                                          className={`h-3.5 w-3.5 rounded text-muted-foreground opacity-0 transition-opacity group-hover/date:opacity-100 hover:bg-muted hover:text-foreground hover:[&_svg]:text-destructive`}
                                          onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleUpdateSummaryComponentsDates(null, c.id, 'eta');
                                          }}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                  </td>

                                  <td className="text-[14px] px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                    <div className="group/date relative inline-flex items-center gap-1">
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button
                                            type="button"
                                            data-qa={rowQa(
                                              `components_modal_${qaToken(c.id || idx)}_received_date_trigger`
                                            )}
                                            className={cn(
                                              'transition hover:text-primary text-left',
                                              formatDate(c.receivedDate) === 'MM/DD/YY'
                                                ? 'text-muted-foreground'
                                                : ''
                                            )}
                                          >
                                            {formatDate(c.receivedDate)}
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent align="end" className="w-auto p-0">
                                          <Calendar
                                            mode="single"
                                            selected={toCalendarDate(c.receivedDate)}
                                            className="rounded-md border-0 p-[12px]"
                                            onSelect={(v) =>
                                              handleUpdateSummaryComponentsDates(
                                                v || null,
                                                c.id,
                                                'receivedDate'
                                              )
                                            }
                                          />
                                        </PopoverContent>
                                      </Popover>
                                      {formatDate(c.receivedDate) !== 'MM/DD/YY' && (
                                        <button
                                          type="button"
                                          data-qa={rowQa(
                                            `components_modal_${qaToken(c.id || idx)}_received_date_clear_trigger`
                                          )}
                                          aria-label="Clear received date"
                                          // className={dateClearButtonAbsoluteClassName}
                                          className={`h-3.5 w-3.5 rounded text-muted-foreground opacity-0 transition-opacity group-hover/date:opacity-100 hover:bg-muted hover:text-foreground hover:[&_svg]:text-destructive`}
                                          onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleUpdateSummaryComponentsDates(
                                              null,
                                              c.id,
                                              'receivedDate'
                                            );
                                          }}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                  </td>

                                  <td className="text-[14px] px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                    <div className="flex justify-center">
                                      <Notebook
                                        row={{
                                          id: notebookRowId,
                                          client: notebookRowClient,
                                          orderType: data?.orderType || null,
                                          itemType: data?.itemType || null,
                                          purchasedGoodsItemType:
                                            data?.purchasedGoodsItemType || null,
                                          roomName: roomName || '',
                                          notebook: Array.isArray(data?.notebook)
                                            ? data.notebook
                                            : [],
                                          archived: Boolean(data?.archived),
                                        }}
                                        defaultTab={
                                          supportsComponentNotebook ? 'components' : 'items'
                                        }
                                        notebookTabs={
                                          supportsComponentNotebook
                                            ? ['projects', 'items', 'components']
                                            : ['projects', 'items']
                                        }
                                        user={accountUser}
                                        table={'items'}
                                        canAccessTeamSettings={canAccessTeamSettings}
                                        componentContext={{
                                          componentId: supportsComponentNotebook
                                            ? componentNotebookId
                                            : null,
                                          componentIdType: supportsComponentNotebook
                                            ? 'summary_component'
                                            : null,
                                        }}
                                        triggerClassName={'h-8 w-8'}
                                        closeOnOutsideClick={false}
                                      />
                                    </div>
                                  </td>
                                </tr>

                                {hardware_has_any_row &&
                                  !c?.fabric &&
                                  c.isStaticHardware &&
                                  ['PARTS & SUPPLIES', 'HARDWARE'].includes(c.item) && (
                                    <tr className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted bg-muted/30">
                                      <td
                                        className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-0"
                                        colSpan={7}
                                      >
                                        <div className="border-l-2 border-primary/30 ml-6 pl-4 py-3">
                                          <div className="text-[12px] font-medium text-muted-foreground mb-2">
                                            {c.item === 'PARTS & SUPPLIES'
                                              ? 'Parts & Supplies Details'
                                              : 'Hardware Details'}
                                          </div>

                                          <div className="grid grid-cols-4 gap-x-4 gap-y-1 text-sm">
                                            <div className="font-medium text-muted-foreground">
                                              Item
                                            </div>
                                            <div className="font-medium text-muted-foreground">
                                              Qty
                                            </div>
                                            <div className="font-medium text-muted-foreground">
                                              Description
                                            </div>
                                            <div className="font-medium text-muted-foreground">
                                              Finish
                                            </div>
                                            {data?.draperyOrderHardware?.rods?.map(
                                              (rod: any, rodIdx: any) => {
                                                if (
                                                  !rod?.description &&
                                                  !rod?.quantity &&
                                                  !rod?.finish
                                                ) {
                                                  return;
                                                }
                                                return (
                                                  <>
                                                    <div
                                                      className="text-muted-foreground"
                                                      key={rodIdx}
                                                    >
                                                      {rod?.item || 'Rods'}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                      {rod?.quantity}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                      {rod?.description}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                      {rod?.finish}
                                                    </div>
                                                  </>
                                                );
                                              }
                                            )}
                                            {data?.draperyOrderHardware?.otherItems?.map(
                                              (other: any, otherIdx: any) => {
                                                if (
                                                  !other?.description &&
                                                  !other?.quantity &&
                                                  !other?.finish
                                                ) {
                                                  return;
                                                }
                                                return (
                                                  <>
                                                    <div
                                                      className="text-muted-foreground"
                                                      key={otherIdx}
                                                    >
                                                      {other?.item}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                      {other?.quantity}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                      {other?.description}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                      {other?.finish}
                                                    </div>
                                                  </>
                                                );
                                              }
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}

                                {headrail_has_any_row && c?.fabric && c.item === 'HEADRAIL' && (
                                  <tr className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted bg-muted/30">
                                    <td
                                      className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-0"
                                      colSpan={7}
                                    >
                                      <div className="border-l-2 border-primary/30 ml-6 pl-4 py-3">
                                        <div className="text-[12px] font-medium text-muted-foreground mb-2">
                                          Headrail Details
                                        </div>

                                        <div className="grid grid-cols-4 gap-x-4 gap-y-1 text-sm">
                                          <div className="font-medium text-muted-foreground">
                                            Item
                                          </div>
                                          <div className="font-medium text-muted-foreground">
                                            Qty
                                          </div>
                                          <div className="font-medium text-muted-foreground">
                                            Description
                                          </div>
                                          <div className="font-medium text-muted-foreground">
                                            Finish
                                          </div>
                                          {data?.draperyOrderHeadrail?.rods?.map(
                                            (rod: any, rodIdx: any) => {
                                              if (
                                                !rod?.description &&
                                                !rod?.quantity &&
                                                !rod?.finish
                                              ) {
                                                return;
                                              }
                                              return (
                                                <>
                                                  <div
                                                    className="text-muted-foreground"
                                                    key={rodIdx}
                                                  >
                                                    {rod?.item || 'Rods'}
                                                  </div>
                                                  <div className="text-muted-foreground">
                                                    {rod?.quantity}
                                                  </div>
                                                  <div className="text-muted-foreground">
                                                    {rod?.description}
                                                  </div>
                                                  <div className="text-muted-foreground">
                                                    {rod?.finish}
                                                  </div>
                                                </>
                                              );
                                            }
                                          )}
                                          {data?.draperyOrderHeadrail?.otherItems?.map(
                                            (other: any, otherIdx: any) => {
                                              if (
                                                !other?.description &&
                                                !other?.quantity &&
                                                !other?.finish
                                              ) {
                                                return;
                                              }
                                              return (
                                                <>
                                                  <div
                                                    className="text-muted-foreground"
                                                    key={otherIdx}
                                                  >
                                                    {other?.item}
                                                  </div>
                                                  <div className="text-muted-foreground">
                                                    {other?.quantity}
                                                  </div>
                                                  <div className="text-muted-foreground">
                                                    {other?.description}
                                                  </div>
                                                  <div className="text-muted-foreground">
                                                    {other?.finish}
                                                  </div>
                                                </>
                                              );
                                            }
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })
                      ) : (
                        <tr className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                          <td className="text-center align-middle p-[16px_6px]" colSpan={7}>
                            No components found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <div>
                    <Button
                      type="button"
                      className="font-medium"
                      variant={'outline'}
                      data-qa={rowQa('components_modal_cancel_trigger')}
                      onClick={() => handleOpenComponents?.(-1)}
                    >
                      Cancel
                    </Button>
                  </div>

                  <div>
                    <Button
                      type="button"
                      // className="components_modal_actions_button save_btn"
                      data-qa={rowQa('components_modal_save_trigger')}
                      disabled={!canEditOrder}
                      onClick={() => handleSaveNewSummaryComponents()}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>

              <div
                className="components_modal_overlay"
                data-qa={rowQa('components_modal_overlay_trigger')}
                onClick={() => handleOpenComponents?.(-1)}
              />
            </>,
            document.body
          )}
      </TableRow>
      <EstimatesMovedDialog
        open={estimatesMovedDialogOpen}
        onOpenChange={setEstimatesMovedDialogOpen}
        onGoToEstimating={onOpenEstimatingTab}
      />
    </>
  );
};
